/**
 * Bulk order: quantities in grid → optional personalize step (one row per garment) → Cart API.
 * Intercepts main Add to cart when inside .product-form.
 */
(function () {
  if (typeof window.themeVariables === 'undefined') {
    return;
  }

  const roots = document.querySelectorAll('[data-bulk-order-table]:not([data-bulk-order-initialized])');
  if (!roots.length) {
    return;
  }

  const cartAddUrl = window.themeVariables.routes.cartAddUrl + '.js';
  const cartUrl = window.themeVariables.routes.cartUrl + '.js';

  const ARTWORK_ID_KEY = '_logo_artwork_id';
  const ARTWORK_FILE_URL_KEY = '_logo_file_url';
  const ARTWORK_FILENAME_KEY = '_logo_original_filename';
  const ARTWORK_UPLOAD_PROP_KEY = '_logo_upload_property_key';
  const ARTWORK_UPLOAD_PROP_VALUE = '_logo_upload_property_value';
  const ARTWORK_SETUP_VARIANT_KEY = '_logo_setup_variant_id';
  const ARTWORK_UPLOAD_WAIT_MS = 20000;
  const ARTWORK_UPLOAD_POLL_MS = 250;
  const ARTWORK_PREVIEW_CACHE_KEY = 'kitwear.logoPreviewByArtwork.v1';
  const ARTWORK_PREVIEW_CACHE_LIMIT = 50;

  /** Uploadly's optional text field is configured as `Notes:` and emits Notes:1, Notes:2, etc. */
  function isUploadlyNotesPropertyKey(key) {
    return /^notes?:?(?:\s*\d+)?$/i.test(String(key || '').trim());
  }

  /**
   * Diagnostic trail for the live Uploadly integration. Never log the artwork hash itself.
   * @param {string} message
   * @param {Record<string, unknown>} [details]
   */
  function artworkDebug(message, details) {
    if (!window.console || typeof window.console.info !== 'function') {
      return;
    }
    if (details) {
      window.console.info('[Kitwear Logo]', message, details);
    } else {
      window.console.info('[Kitwear Logo]', message);
    }
  }

  /**
   * Keep the original browser File alive even if Uploadly replaces/clears its input after processing.
   * The capture-phase change listener below runs before normal bubbling app listeners.
   * @type {WeakMap<HTMLElement, { file: File; fingerprint: Promise<string>; hash: string }>}
   */
  const capturedArtworkFiles = new WeakMap();
  const artworkReadinessSnapshots = new WeakMap();
  const bulkAddsInFlight = new WeakSet();

  /**
   * @param {File} file
   * @returns {Promise<string>}
   */
  async function sha256ArtworkFile(file) {
    if (!(file instanceof File) || !window.crypto || !window.crypto.subtle) {
      throw new Error('Original artwork file bytes are unavailable for SHA-256.');
    }
    const bytes = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * @param {HTMLElement} root
   * @param {File} file
   */
  function captureArtworkFile(root, file) {
    artworkDebug('Original File object captured', {
      filename: file.name,
      size: file.size,
      type: file.type || 'unknown',
    });
    const fingerprint = sha256ArtworkFile(file)
      .then((hash) => {
        artworkDebug('SHA-256 generated from File.arrayBuffer()', {
          filename: file.name,
          digestLength: hash.length,
        });
        return hash;
      })
      .catch((error) => {
        artworkDebug('SHA-256 generation failed', {
          filename: file.name,
          reason: error instanceof Error ? error.message : String(error),
        });
        throw error;
      });
    const capture = { file, fingerprint, hash: '' };
    capturedArtworkFiles.set(root, capture);
    fingerprint.then((hash) => {
      if (capturedArtworkFiles.get(root) === capture) {
        capture.hash = hash;
        refreshArtworkReadyState(root);
      }
    }).catch(() => {});
    // Avoid an unhandled rejection before Add to cart awaits the result.
    fingerprint.catch(() => {});
  }

  /** @param {Event} event */
  function fileInputFromEvent(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return (
      path.find((node) => node instanceof HTMLInputElement && node.type === 'file') ||
      (event.target instanceof HTMLInputElement && event.target.type === 'file' ? event.target : null)
    );
  }

  /** @param {HTMLInputElement} input */
  function artworkRootForFileInput(input) {
    let productForm = input.closest('.product-form');
    const inputRoot = input.getRootNode();
    if (!productForm && inputRoot instanceof ShadowRoot && inputRoot.host instanceof HTMLElement) {
      productForm = inputRoot.host.closest('.product-form');
    }
    let root = productForm && productForm.querySelector('[data-bulk-order-table]');
    if (!(root instanceof HTMLElement)) {
      const eligibleRoots = Array.from(roots).filter((candidate) =>
        isEligibleArtworkKitMode(/** @type {HTMLElement} */ (candidate))
      );
      root = eligibleRoots.length === 1 ? eligibleRoots[0] : null;
    }
    return root instanceof HTMLElement ? root : null;
  }

  /** @param {Event} event */
  function captureArtworkFileEvent(event) {
    const input = fileInputFromEvent(event);
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    const root = artworkRootForFileInput(input);
    if (!(root instanceof HTMLElement)) {
      artworkDebug('Native file input detected but no eligible kit root was found', {
        inputName: input.name || '',
        inputId: input.id || '',
      });
      return;
    }
    const file = input.files && input.files.length ? input.files[0] : null;
    if (!(file instanceof File)) {
      return;
    }
    const remembered = capturedArtworkFiles.get(root);
    if (remembered && remembered.file === file) {
      return;
    }
    artworkDebug('Native file input detected', {
      inputName: input.name || '',
      inputId: input.id || '',
      filename: file.name,
    });
    captureArtworkFile(root, file);
  }

  // `input` fires before many widget handlers; `change` covers browsers/widgets that omit it.
  document.addEventListener('input', captureArtworkFileEvent, true);
  document.addEventListener('change', captureArtworkFileEvent, true);

  /**
   * Shopify `/cart/add.js` failures often put text in `errors` (string or object), not `description`.
   * @param {unknown} json
   * @param {number} status
   * @returns {string}
   */
  function messageFromCartAddError(json, status) {
    if (!json || typeof json !== 'object') {
      return status ? `Request failed (${status}).` : '';
    }
    const j = /** @type {Record<string, unknown>} */ (json);
    if (typeof j.description === 'string' && j.description.trim()) {
      return j.description.trim();
    }
    if (typeof j.message === 'string' && j.message.trim()) {
      return j.message.trim();
    }
    const err = j.errors;
    if (typeof err === 'string' && err.trim()) {
      return err.trim();
    }
    if (Array.isArray(err)) {
      const parts = err.map((x) => (x != null ? String(x).trim() : '')).filter(Boolean);
      if (parts.length) {
        return parts.join(' ');
      }
    }
    if (err && typeof err === 'object') {
      const parts = [];
      for (const k of Object.keys(/** @type {Record<string, unknown>} */ (err))) {
        const v = /** @type {Record<string, unknown>} */ (err)[k];
        if (Array.isArray(v)) {
          v.forEach((x) => {
            if (x != null && String(x).trim()) {
              parts.push(String(x).trim());
            }
          });
        } else if (v != null && String(v).trim()) {
          parts.push(String(v).trim());
        }
      }
      if (parts.length) {
        return parts.join(' ');
      }
    }
    return '';
  }

  /**
   * Shopify often returns the useless string "Cart Error" — swap for a merchant hint when present on root.
   * @param {string} msg
   * @param {HTMLElement} root
   * @returns {string}
   */
  function humanizeCartError(msg, root) {
    const t = (msg || '').trim();
    if (t === 'Cart Error' || t === '') {
      const hint = root.getAttribute('data-msg-cart-error-hint');
      if (hint && hint.trim()) {
        return hint.trim();
      }
    }
    return msg || '';
  }

  /** Shopify line item property values are limited to 255 characters. */
  const LINE_ITEM_PROPERTY_MAX_LEN = 255;

  /**
   * @param {HTMLElement} root
   */
  function isNewLogoChosen(root) {
    const sponsorPanel = root.querySelector('[data-bulk-sponsor-panel]');
    const workwearFeePanel = root.querySelector('[data-bulk-logo-fee-panel]');
    const teamNewChosen = sponsorPanel
      ? sponsorPanel.querySelector('input[data-bulk-sponsor-radio][value="new"]:checked')
      : null;
    const workwearNewChosen =
      workwearFeePanel && workwearFeePanel.querySelector('input[data-bulk-logo-fee-cb]:checked');
    return Boolean(teamNewChosen || workwearNewChosen);
  }

  /**
   * @param {HTMLElement} root
   */
  function isReusedLogoChosen(root) {
    const sponsorPanel = root.querySelector('[data-bulk-sponsor-panel]');
    return Boolean(
      sponsorPanel && sponsorPanel.querySelector('input[data-bulk-sponsor-radio][value="reuse"]:checked')
    );
  }

  /**
   * @param {HTMLElement} root
   */
  function isNewArtworkChosen(root) {
    return isNewLogoChosen(root) || isReusedLogoChosen(root);
  }

  /**
   * @param {HTMLElement} root
   */
  function isEligibleArtworkKitMode(root) {
    const mode = (root.getAttribute('data-bulk-kit-mode') || '').trim().toLowerCase();
    return (
      mode === 'team_kit' ||
      mode === 'training_kit_with_sponsor' ||
      root.getAttribute('data-bulk-default-application') === 'true' ||
      root.getAttribute('data-bulk-training-wear-application') === 'true'
    );
  }

  /**
   * Chargeable Print / Embroidery application logic is restricted to the unsuffixed
   * Default Product template marker emitted by Liquid.
   * @param {HTMLElement} root
   */
  function isDefaultApplicationProduct(root) {
    return root.getAttribute('data-bulk-default-application') === 'true';
  }

  /**
   * Fixed-price printing logic is isolated to the exact Training Wear template marker.
   * @param {HTMLElement} root
   */
  function isTrainingWearApplicationProduct(root) {
    return root.getAttribute('data-bulk-training-wear-application') === 'true';
  }

  /**
   * @param {HTMLElement} root
   */
  function isLogoPricingSelectionActive(root) {
    const sponsorPanel = root.querySelector('[data-bulk-sponsor-panel]');
    if (sponsorPanel) {
      return Boolean(sponsorPanel.querySelector('input[data-bulk-sponsor-radio]:checked'));
    }
    const workwearFeePanel = root.querySelector('[data-bulk-logo-fee-panel]');
    return Boolean(
      workwearFeePanel && workwearFeePanel.querySelector('input[data-bulk-logo-fee-cb]:checked')
    );
  }

  /**
   * @param {HTMLElement} root
   */
  function isClubBadgeFeatureEnabled(root) {
    return root.getAttribute('data-bulk-club-badge-enabled') === 'true';
  }

  /**
   * @param {HTMLElement} root
   */
  function isClubBadgeEmbroideryChosen(root) {
    if (!isClubBadgeFeatureEnabled(root)) {
      return false;
    }
    const selected = root.querySelector('[data-bulk-club-badge-method]:checked');
    if (selected instanceof HTMLInputElement) {
      return selected.value === 'embroidered';
    }
    return (root.getAttribute('data-bulk-club-badge-method') || 'printed') === 'embroidered';
  }

  /**
   * @param {unknown} responseJson
   * @param {number} status
   * @param {HTMLElement} root
   */
  function cartAddFailureMessage(responseJson, status, root) {
    if (status === 403) {
      const forbidden = root.getAttribute('data-msg-cart-error-forbidden');
      if (forbidden && forbidden.trim()) {
        return forbidden.trim();
      }
    }
    const raw = messageFromCartAddError(responseJson, status);
    const human = humanizeCartError(raw, root);
    if (human && human.trim()) {
      return human.trim();
    }
    if (status) {
      return `Could not add to cart (HTTP ${status}).`;
    }
    const generic = root.getAttribute('data-msg-error');
    console.warn('[kitwear bulk add] cart add failed without a clear message', {
      status,
      responseJson,
    });
    return generic && generic.trim() ? generic.trim() : 'Could not add to cart';
  }

  /**
   * @returns {Promise<Record<string, unknown> | null>}
   */
  async function fetchCartJson() {
    try {
      const cartRes = await fetch(cartUrl, { credentials: 'same-origin' });
      if (!cartRes.ok) {
        return null;
      }
      return await cartRes.json();
    } catch {
      return null;
    }
  }

  /**
   * @param {unknown} line
   * @returns {string}
   */
  function getLineItemBulkGroup(line) {
    if (!line || typeof line !== 'object') {
      return '';
    }
    const p = /** @type {{ properties?: unknown }} */ (line).properties;
    if (!p) {
      return '';
    }
    if (typeof p === 'object' && !Array.isArray(p)) {
      const o = /** @type {Record<string, unknown>} */ (p);
      const g = o._kitwear_bulk_group ?? o.kitwear_bulk_group;
      return g != null ? String(g).trim() : '';
    }
    if (Array.isArray(p)) {
      const row = p.find(
        (x) =>
          x &&
          typeof x === 'object' &&
          'name' in x &&
          (/** @type {{ name?: string }} */ (x).name === '_kitwear_bulk_group' ||
            /** @type {{ name?: string }} */ (x).name === 'kitwear_bulk_group')
      );
      if (row && 'value' in /** @type {object} */ (row)) {
        const v = /** @type {{ value?: unknown }} */ (row).value;
        return v != null ? String(v).trim() : '';
      }
    }
    return '';
  }

  /**
   * Remove cart lines that share this bulk-add group (after a failed follow-up add).
   * @param {string} bulkGroupId
   */
  async function rollbackBulkGroupLines(bulkGroupId) {
    const cart = await fetchCartJson();
    if (!cart) {
      return;
    }
    const changeUrl = window.themeVariables.routes.cartChangeUrl + '.js';
    const lines = (cart.items || []).filter((item) => getLineItemBulkGroup(item) === bulkGroupId);
    lines.sort((a, b) => (b.line || 0) - (a.line || 0));
    for (const item of lines) {
      const lineNum = item.line;
      if (lineNum == null) {
        continue;
      }
      await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ line: lineNum, quantity: 0 }),
      });
    }
  }

  /** @type {number} */
  const CART_ADD_CHUNK_SIZE = 20;

  /**
   * @param {Record<string, unknown>} body
   * @returns {Promise<{ ok: boolean; response: Response; responseJson: unknown }>}
   */
  async function postCartAddJson(body) {
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    /** @type {unknown} */
    let responseJson = null;
    try {
      responseJson = await response.json();
    } catch {
      responseJson = null;
    }
    return { ok: response.ok, response, responseJson };
  }

  /**
   * @param {number} ms
   */
  function delay(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  /**
   * Add lines one at a time (fallback when batch add is blocked on preview / WAF).
   * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
   * @param {string} bulkGroupId
   */
  async function postCartAddItemsSequential(items, bulkGroupId) {
    /** @type {{ ok: boolean; response: Response; responseJson: unknown }} */
    let last = { ok: true, response: new Response(), responseJson: null };
    for (let i = 0; i < items.length; i++) {
      last = await postCartAddJson({ items: [items[i]] });
      if (!last.ok) {
        if (bulkGroupId) {
          await rollbackBulkGroupLines(bulkGroupId);
        }
        return last;
      }
      if (i < items.length - 1 && items.length > 3) {
        await delay(100);
      }
    }
    return last;
  }

  /**
   * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
   * @param {{ bulkGroupId?: string }} [options]
   * @returns {Promise<{ ok: boolean; response: Response; responseJson: unknown }>}
   */
  async function postCartAddItems(items, options = {}) {
    const bulkGroupId = options.bulkGroupId || '';
    const useDrawerSections = window.themeVariables?.settings?.cartType === 'drawer';

    /** @type {Array<{ ok: boolean; response: Response; responseJson: unknown }>} */
    const chunkResults = [];
    for (let offset = 0; offset < items.length; offset += CART_ADD_CHUNK_SIZE) {
      const chunk = items.slice(offset, offset + CART_ADD_CHUNK_SIZE);
      const body = { items: chunk };
      if (useDrawerSections && offset + chunk.length >= items.length) {
        body.sections = 'mini-cart';
      }
      const result = await postCartAddJson(body);
      chunkResults.push(result);
      if (!result.ok) {
        const status = result.response.status;
        if (status === 403 || status === 502 || status === 429) {
          if (bulkGroupId) {
            await rollbackBulkGroupLines(bulkGroupId);
          }
          return postCartAddItemsSequential(items, bulkGroupId);
        }
        if (bulkGroupId) {
          await rollbackBulkGroupLines(bulkGroupId);
        }
        return result;
      }
    }

    const last = chunkResults[chunkResults.length - 1];
    return last || { ok: true, response: new Response(), responseJson: null };
  }

  /**
   * @param {Record<string, unknown>} responseJson
   */
  async function refreshCartUi(responseJson) {
    const cartContent = await fetchCartJson();
    if (!cartContent) {
      return;
    }
    let sections =
      responseJson && typeof responseJson === 'object' && 'sections' in responseJson
        ? /** @type {Record<string, unknown>} */ (responseJson).sections
        : null;

    if (!sections && window.themeVariables?.settings?.cartType === 'drawer') {
      try {
        const sectionRes = await fetch(`${window.themeVariables.routes.cartUrl}?sections=mini-cart`, {
          credentials: 'same-origin',
        });
        const sectionJson = await sectionRes.json();
        if (sectionJson && sectionJson.sections) {
          sections = sectionJson.sections;
        }
      } catch {
        // Drawer HTML refresh is optional; cart count still updates via cart.js.
      }
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent },
      })
    );
    if (sections) {
      cartContent.sections = sections;
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          openMiniCart: window.themeVariables?.settings?.cartType === 'drawer',
        },
      })
    );
  }

  /**
   * @param {HTMLElement} root
   */
  function parseVariantMap(root) {
    const raw = root.querySelector('[data-bulk-variant-json]');
    if (!raw || !raw.textContent) {
      return [];
    }
    try {
      return JSON.parse(raw.textContent);
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function parseIndices(root) {
    const sizeIdx = parseInt(root.dataset.bulkSizeIdx, 10);
    const colorIdx = parseInt(root.dataset.bulkColorIdx, 10);
    const useColor = root.dataset.bulkUseColor === 'true';
    const extraIndices = (root.dataset.bulkExtraIndices || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n));
    return {
      sizeIdx: Number.isNaN(sizeIdx) ? 0 : sizeIdx,
      colorIdx: Number.isNaN(colorIdx) ? 0 : colorIdx,
      useColor,
      extraIndices,
    };
  }

  /**
   * @param {Record<string, unknown>} row
   * @param {string} sizeValue
   * @param {string} colorValue
   * @param {Record<number, string>} extraSelections
   * @param {ReturnType<typeof parseIndices>} idx
   */
  function rowMatches(row, sizeValue, colorValue, extraSelections, idx) {
    if (row.opts && Array.isArray(row.opts)) {
      const { sizeIdx, colorIdx, useColor, extraIndices } = idx;
      if (row.opts[sizeIdx] !== sizeValue) {
        return false;
      }
      if (useColor && row.opts[colorIdx] !== colorValue) {
        return false;
      }
      for (const ei of extraIndices) {
        const want = extraSelections[ei];
        if (want != null && row.opts[ei] !== want) {
          return false;
        }
      }
      return true;
    }
    if (idx.extraIndices.length > 0) {
      return false;
    }
    const colorMatch = !colorValue || row.c === colorValue;
    return colorMatch && row.s === sizeValue;
  }

  /**
   * @param {Array<Record<string, unknown>>} rows
   * @param {string} sizeValue
   * @param {string} colorValue
   * @param {Record<number, string>} extraSelections
   * @param {ReturnType<typeof parseIndices>} idx
   */
  function findVariantRow(rows, sizeValue, colorValue, extraSelections, idx) {
    return rows.find((r) => rowMatches(r, sizeValue, colorValue, extraSelections, idx));
  }

  /**
   * @param {HTMLElement} root
   */
  function getExtraSelections(root) {
    /** @type {Record<number, string>} */
    const map = {};
    root.querySelectorAll('[data-bulk-extra-select]').forEach((sel) => {
      const el = /** @type {HTMLSelectElement} */ (sel);
      const i = parseInt(el.getAttribute('data-option-index') || '', 10);
      if (!Number.isNaN(i)) {
        map[i] = el.value;
      }
    });
    return map;
  }

  /**
   * @param {HTMLFormElement | null} form
   */
  function collectPropertiesFromForm(form) {
    const props = {};
    if (!form) {
      return props;
    }
    const fd = new FormData(form);
    for (const [key, value] of fd.entries()) {
      if (typeof value !== 'string') {
        continue;
      }
      const m = key.match(/^properties\[(.+)\]$/);
      if (m) {
        props[m[1]] = value;
      }
    }
    return props;
  }

  /**
   * Query light DOM plus any accessible open shadow roots.
   * @param {ParentNode} scope
   * @param {string} selector
   * @returns {Element[]}
   */
  function queryAllAcrossOpenRoots(scope, selector) {
    const found = new Set();
    const visit = (current) => {
      if (!current || typeof current.querySelectorAll !== 'function') {
        return;
      }
      current.querySelectorAll(selector).forEach((element) => found.add(element));
      current.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) {
          visit(element.shadowRoot);
        }
      });
    };
    visit(scope);
    return Array.from(found);
  }

  /**
   * Uploadly may render its eventual `properties[...]` input beside the native product form.
   * Collect only real named line-item property controls; previews are not accepted as metadata.
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   * @returns {Record<string, string>}
   */
  function collectArtworkUploadProperties(root, form) {
    const props = collectPropertiesFromForm(form);
    const wrapper = root.closest('.product-form');
    const scopes = [wrapper, form, root].filter(Boolean);
    const fields = new Set();
    scopes.forEach((scope) => {
      queryAllAcrossOpenRoots(scope, '[name^="properties["]').forEach((field) => fields.add(field));
    });
    fields.forEach((field) => {
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLTextAreaElement ||
          field instanceof HTMLSelectElement
        ) ||
        field.disabled
      ) {
        return;
      }
      if (
        field instanceof HTMLInputElement &&
        (field.type === 'checkbox' || field.type === 'radio') &&
        !field.checked
      ) {
        return;
      }
      const match = String(field.name || '').match(/^properties\[(.+)\]$/);
      const value = String(field.value || '').trim();
      if (match && value && !isUploadlyNotesPropertyKey(match[1])) {
        props[match[1]] = value;
      }
    });
    return props;
  }

  /**
   * @param {string} s
   */
  function normalizeAmpersandEntities(s) {
    let t = String(s).trim();
    let prev = '';
    while (prev !== t) {
      prev = t;
      t = t.replace(/&amp;/gi, '&');
    }
    return t;
  }

  /**
   * Line-item property value looks like a hosted file / image URL (Uploadly, Shopify files, etc.).
   * @param {string} val
   */
  function looksLikeUploadPropertyUrl(val) {
    const t = normalizeAmpersandEntities(val);
    if (!t || t.includes('\n')) {
      return false;
    }
    const abs = t.startsWith('//') ? `https:${t}` : t;
    if (!/^https?:\/\//i.test(abs)) {
      return false;
    }
    const lower = abs.toLowerCase();
    if (lower.includes('ucarecdn.com') || lower.includes('uploadcare.com') || lower.includes('ucare.co/')) {
      return true;
    }
    if (lower.includes('uploadly')) {
      return true;
    }
    // Uploadly's current direct-upload completion property uses this host without a file extension.
    if (lower.includes('cdn.upstorage.io')) {
      return true;
    }
    if (lower.includes('ph_image=')) {
      return true;
    }
    if (lower.includes('cdn.shopify.com') && (lower.includes('/files/') || lower.includes('/s/files/'))) {
      return true;
    }
    if (lower.includes('shopifycdn.com')) {
      return true;
    }
    if (lower.includes('shop.app') && lower.includes('file')) {
      return true;
    }
    if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|pdf|ai|eps)(\?|#|&|$)/i.test(lower)) {
      return true;
    }
    return false;
  }

  /**
   * Upload apps may store the clickable URL as a raw URL, HTML anchor, or JSON/string payload.
   * This extracts only the file reference; it is never used as the artwork identity.
   * @param {string} value
   * @returns {string}
   */
  function extractUploadPropertyUrl(value) {
    const raw = normalizeAmpersandEntities(String(value || '')).replace(/\\\//g, '/').trim();
    if (!raw) {
      return '';
    }
    const candidates = [raw];
    try {
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      doc.querySelectorAll('[href], [src]').forEach((element) => {
        const candidate = element.getAttribute('href') || element.getAttribute('src');
        if (candidate) candidates.push(candidate);
      });
    } catch {
      // Continue with URL substring extraction below.
    }
    const matches = raw.match(/(?:https?:)?\/\/[^\s"'<>\\]+/gi) || [];
    candidates.push(...matches);
    for (const candidate of candidates) {
      const cleaned = normalizeAmpersandEntities(candidate).replace(/[),.;]+$/, '').trim();
      if (looksLikeUploadPropertyUrl(cleaned)) {
        return cleaned.startsWith('//') ? `https:${cleaned}` : cleaned;
      }
    }
    return '';
  }

  /**
   * Find the actual Uploadly/app line-item property. The value is retained verbatim.
   * @param {Record<string, string>} props
   * @returns {{ key: string; value: string; fileUrl: string } | null}
   */
  function findHumanUploadProperty(props) {
    const candidates = [];
    Object.entries(props || {}).forEach(([key, rawValue]) => {
      const value = String(rawValue || '').trim();
      const lowerKey = String(key || '').trim().toLowerCase();
      const fileUrl = extractUploadPropertyUrl(value);
      if (!key || key.charAt(0) === '_' || lowerKey === 'logo position' || !fileUrl) {
        return;
      }
      let priority = 10;
      if (lowerKey.includes('upload')) priority = 0;
      else if (lowerKey.includes('artwork')) priority = 1;
      else if (lowerKey.includes('logo')) priority = 2;
      else if (lowerKey.includes('image') || lowerKey.includes('file')) priority = 3;
      candidates.push({ key, value, fileUrl, priority });
    });
    candidates.sort((a, b) => a.priority - b.priority);
    return candidates.length
      ? { key: candidates[0].key, value: candidates[0].value, fileUrl: candidates[0].fileUrl }
      : null;
  }

  /**
   * Uploadly's public `cdn.upstorage.io/u/...` value is an HTML download page, not an image.
   * Its successful uploader input separately exposes the renderable Uploadcare CDN source.
   * @param {string} value
   * @returns {string}
   */
  function normalizeUploadlyPreviewCdnUrl(value) {
    const raw = normalizeAmpersandEntities(String(value || '')).trim();
    if (!raw) return '';
    try {
      const absolute = raw.startsWith('//') ? `https:${raw}` : raw;
      const url = /^https?:\/\//i.test(absolute)
        ? new URL(absolute)
        : new URL(absolute, window.location.href);
      const host = url.hostname.toLowerCase();
      if (host !== 'ucarecdn.com' && !host.endsWith('.ucarecdn.com') && !host.includes('uploadcare')) {
        return '';
      }
      const uuidMatch = url.pathname.match(/^\/([0-9a-f-]{36})(?:\/|$)/i);
      if (uuidMatch) {
        // Trim transparent/solid outer canvas before fitting the visible artwork into the tile.
        return `${url.origin}/${uuidMatch[1]}/-/trim/10/8p/-/preview/160x160/-/quality/lightest/`;
      }
    } catch {
      // Ignore malformed third-party values and use the generic file tile.
    }
    return '';
  }

  /**
   * Recover a directly renderable image embedded in older Uploadly/Uploadcare property formats.
   * Bare upstorage download-page URLs deliberately do not qualify.
   * @param {string} value
   * @param {number} [depth]
   * @returns {string}
   */
  function reusablePreviewUrlFromProperty(value, depth = 0) {
    if (depth > 2) return '';
    const raw = normalizeAmpersandEntities(String(value || '')).replace(/\\\//g, '/').trim();
    if (!raw) return '';
    const candidates = [raw];
    try {
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      doc.querySelectorAll('[href], [src]').forEach((element) => {
        const candidate = element.getAttribute('src') || element.getAttribute('href');
        if (candidate) candidates.push(candidate);
      });
    } catch {
      // Continue with URL extraction.
    }
    candidates.push(...(raw.match(/(?:https?:)?\/\/[^\s"'<>\\]+/gi) || []));
    for (const candidate of candidates) {
      const cleaned = normalizeAmpersandEntities(candidate).replace(/[),.;]+$/, '').trim();
      const uploadlyCdn = normalizeUploadlyPreviewCdnUrl(cleaned);
      if (uploadlyCdn) return uploadlyCdn;
      try {
        const absolute = cleaned.startsWith('//') ? `https:${cleaned}` : cleaned;
        const url = /^https?:\/\//i.test(absolute)
          ? new URL(absolute)
          : new URL(absolute, window.location.href);
        for (const key of ['ph_image', 'image', 'src', 'url']) {
          const parameter = url.searchParams.get(key);
          if (!parameter) continue;
          let decoded = parameter;
          try {
            decoded = decodeURIComponent(parameter.replace(/\+/g, ' '));
          } catch {
            // Use the raw parameter.
          }
          const nested = reusablePreviewUrlFromProperty(decoded, depth + 1);
          if (nested) return nested;
        }
        const host = url.hostname.toLowerCase();
        if (
          host !== 'cdn.upstorage.io' &&
          !host.includes('uploadly') &&
          /\.(jpg|jpeg|png|gif|webp|svg|bmp|avif)$/i.test(url.pathname)
        ) {
          return url.href;
        }
      } catch {
        // Continue to the next candidate.
      }
    }
    return '';
  }

  /** @returns {Record<string, string>} */
  function readArtworkPreviewCache() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(ARTWORK_PREVIEW_CACHE_KEY) || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  /** @param {string} artworkId */
  function cachedArtworkPreviewUrl(artworkId) {
    const id = String(artworkId || '').trim().toLowerCase();
    if (!/^[a-f0-9]{64}$/.test(id)) return '';
    return normalizeUploadlyPreviewCdnUrl(readArtworkPreviewCache()[id] || '');
  }

  /**
   * Persist only the thumbnail mapping in this browser so it survives product-page refreshes.
   * No cart property or artwork identity is changed.
   * @param {string} artworkId
   * @param {string} previewUrl
   */
  function rememberArtworkPreviewUrl(artworkId, previewUrl) {
    const id = String(artworkId || '').trim().toLowerCase();
    const normalized = normalizeUploadlyPreviewCdnUrl(previewUrl);
    if (!/^[a-f0-9]{64}$/.test(id) || !normalized) return;
    try {
      const cache = readArtworkPreviewCache();
      delete cache[id];
      cache[id] = normalized;
      const ids = Object.keys(cache);
      ids.slice(0, Math.max(0, ids.length - ARTWORK_PREVIEW_CACHE_LIMIT)).forEach((oldId) => {
        delete cache[oldId];
      });
      window.localStorage.setItem(ARTWORK_PREVIEW_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // Storage can be unavailable in privacy modes; the current page can still use the URL.
    }
  }

  /**
   * Read Uploadly's actual runtime preview source (`data-cdn`), which differs from its public file link.
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   * @param {{ key: string; value: string }} uploadProperty
   */
  function uploadlyRuntimePreviewUrl(root, form, uploadProperty) {
    const wrapper = root.closest('.product-form');
    const scopes = [wrapper, form, root].filter(Boolean);
    const fields = new Set();
    scopes.forEach((scope) => {
      queryAllAcrossOpenRoots(scope, '[name^="properties["]').forEach((field) => fields.add(field));
    });
    const expectedName = `properties[${uploadProperty.key}]`;
    for (const field of fields) {
      if (!(field instanceof HTMLInputElement)) continue;
      if (field.name !== expectedName || String(field.value || '').trim() !== uploadProperty.value) continue;
      const previewUrl = normalizeUploadlyPreviewCdnUrl(field.getAttribute('data-cdn') || '');
      if (previewUrl) return previewUrl;
    }
    return reusablePreviewUrlFromProperty(uploadProperty.value);
  }

  /**
   * Wait for Uploadly's asynchronous completion field instead of validating immediately.
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   */
  async function waitForHumanUploadProperty(root, form) {
    const startedAt = Date.now();
    artworkDebug('Waiting for Uploadly properties[...] completion field', {
      timeoutMs: ARTWORK_UPLOAD_WAIT_MS,
      uploadConsideredReady: false,
    });
    while (Date.now() - startedAt <= ARTWORK_UPLOAD_WAIT_MS) {
      const props = collectArtworkUploadProperties(root, form);
      const uploadProperty = findHumanUploadProperty(props);
      if (uploadProperty) {
        artworkDebug('Uploadly upload completion detected', {
          propertyKey: uploadProperty.key,
          propertyValue: uploadProperty.value,
          uploadConsideredReady: true,
        });
        refreshArtworkReadyState(root);
        return uploadProperty;
      }
      await new Promise((resolve) => window.setTimeout(resolve, ARTWORK_UPLOAD_POLL_MS));
    }
    const observedProps = collectArtworkUploadProperties(root, form);
    artworkDebug('Add-to-cart validation failed: Uploadly completion property timed out', {
      observedPropertyKeys: Object.keys(observedProps),
      timeoutMs: ARTWORK_UPLOAD_WAIT_MS,
      uploadConsideredReady: false,
    });
    return null;
  }

  /** Clear only an upload-readiness error previously shown by an intentional Add attempt. */
  function clearLogoUploadValidationError(root) {
    const status = root.querySelector('[data-bulk-status]');
    if (!(status instanceof HTMLElement) || status.dataset.kitwearStatusSource !== 'logo-upload') {
      return;
    }
    status.hidden = true;
    status.textContent = '';
    delete status.dataset.kitwearStatusSource;
    artworkDebug('Prior upload validation error cleared after upload became ready');
  }

  /**
   * Passive state tracking only. This never shows customer-facing validation.
   * @param {HTMLElement} root
   */
  function refreshArtworkReadyState(root) {
    if (!isEligibleArtworkKitMode(root)) {
      return false;
    }
    const capture = capturedArtworkFiles.get(root);
    const shaReady = Boolean(capture && /^[a-f0-9]{64}$/.test(capture.hash));
    const form = resolveMainForm(root, null);
    const uploadProperty = findHumanUploadProperty(collectArtworkUploadProperties(root, form));
    const ready = Boolean(shaReady && uploadProperty);
    const signature = JSON.stringify({
      capturedFilePresent: Boolean(capture && capture.file instanceof File),
      shaReady,
      propertyKey: uploadProperty ? uploadProperty.key : '',
      propertyValue: uploadProperty ? uploadProperty.value : '',
      uploadConsideredReady: ready,
    });
    if (artworkReadinessSnapshots.get(root) !== signature) {
      artworkReadinessSnapshots.set(root, signature);
      artworkDebug('Upload readiness state changed', JSON.parse(signature));
    }
    if (ready) {
      clearLogoUploadValidationError(root);
    }
    return ready;
  }

  /**
   * Uploadly appends/replaces its hidden property asynchronously. Observe state without invoking validation.
   * @param {HTMLElement} root
   */
  function initArtworkReadinessTracking(root) {
    if (!isEligibleArtworkKitMode(root) || root.dataset.kitwearLogoReadinessBound === 'true') {
      return;
    }
    const wrapper = root.closest('.product-form');
    if (!wrapper) {
      return;
    }
    root.dataset.kitwearLogoReadinessBound = 'true';
    const observer = new MutationObserver(() => {
      refreshArtworkReadyState(root);
    });
    observer.observe(wrapper, { childList: true, subtree: true });
    refreshArtworkReadyState(root);
  }

  /**
   * @param {string} value
   */
  function filenameFromUploadValue(value) {
    const raw = normalizeAmpersandEntities(String(value || '')).trim();
    if (!raw) {
      return '';
    }
    try {
      const absolute = raw.startsWith('//') ? `https:${raw}` : raw;
      const url = new URL(absolute, window.location.origin);
      const embedded = url.searchParams.get('ph_image');
      if (embedded) {
        const nested = filenameFromUploadValue(embedded);
        if (nested) return nested;
      }
      const last = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || '').trim();
      if (last && last.includes('.')) {
        return last.slice(0, LINE_ITEM_PROPERTY_MAX_LEN);
      }
    } catch {
      // The Uploadly value may be a non-standard viewer URL; use the final path token below.
    }
    const token = raw.split(/[/?#]/).filter(Boolean).pop() || '';
    try {
      return decodeURIComponent(token).slice(0, LINE_ITEM_PROPERTY_MAX_LEN);
    } catch {
      return token.slice(0, LINE_ITEM_PROPERTY_MAX_LEN);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   * @returns {{ file: File; fingerprint: Promise<string>; hash: string } | null}
   */
  function originalArtworkFileCapture(root, form) {
    const remembered = capturedArtworkFiles.get(root);
    if (remembered && remembered.file instanceof File) {
      return remembered;
    }
    const productForm = root.closest('.product-form');
    const uploadGate = productForm && productForm.querySelector('[data-uploadly-sponsor-gate]');
    const scopes = [uploadGate, productForm, form].filter(Boolean);
    for (const scope of scopes) {
      const inputs = queryAllAcrossOpenRoots(scope, 'input[type="file"]');
      for (const input of inputs) {
        if (input instanceof HTMLInputElement && input.files && input.files[0] instanceof File) {
          artworkDebug('Native file input recovered during add-to-cart validation', {
            inputName: input.name || '',
            inputId: input.id || '',
            filename: input.files[0].name,
          });
          captureArtworkFile(root, input.files[0]);
          return capturedArtworkFiles.get(root) || null;
        }
      }
    }
    return null;
  }

  /**
   * @param {unknown} cart
   * @returns {Map<string, { id: string; fileUrl: string; previewUrl: string; filename: string; uploadPropertyKey: string; uploadPropertyValue: string; firstProduct: string; setupVariantId: string }>}
   */
  function reusableArtworksFromCart(cart) {
    const artworks = new Map();
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach((item) => {
      const props = normalizeLineItemProperties(item.properties || {});
      if (String(props._kitwear_fee_type || '').trim()) {
        return;
      }
      const id = String(props[ARTWORK_ID_KEY] || '').trim().toLowerCase();
      if (!/^[a-f0-9]{64}$/.test(id)) {
        return;
      }
      const uploadProperty = findHumanUploadProperty(props);
      const fileUrl =
        (uploadProperty ? uploadProperty.fileUrl : '') ||
        extractUploadPropertyUrl(String(props[ARTWORK_FILE_URL_KEY] || '')) ||
        extractUploadPropertyUrl(String(props[ARTWORK_UPLOAD_PROP_VALUE] || ''));
      if (!fileUrl) {
        return;
      }
      const filename = String(props[ARTWORK_FILENAME_KEY] || filenameFromUploadValue(fileUrl) || 'Uploaded artwork')
        .trim()
        .slice(0, LINE_ITEM_PROPERTY_MAX_LEN);
      const uploadPropertyKey = String(
        (uploadProperty ? uploadProperty.key : '') || props[ARTWORK_UPLOAD_PROP_KEY] || 'Image Upload'
      ).trim();
      const uploadPropertyValue = String(
        (uploadProperty ? uploadProperty.value : '') ||
          (uploadPropertyKey && props[uploadPropertyKey]) ||
          props[ARTWORK_UPLOAD_PROP_VALUE] ||
          fileUrl
      ).trim();
      const firstProduct = String(item.product_title || item.title || 'Kit item').trim();
      if (!artworks.has(id)) {
        artworks.set(id, {
          id,
          fileUrl,
          previewUrl:
            cachedArtworkPreviewUrl(id) ||
            reusablePreviewUrlFromProperty(uploadProperty ? uploadProperty.value : fileUrl),
          filename,
          uploadPropertyKey,
          uploadPropertyValue,
          firstProduct,
          setupVariantId: String(props[ARTWORK_SETUP_VARIANT_KEY] || '').trim(),
        });
      }
    });
    return artworks;
  }

  /**
   * @param {HTMLElement} root
   * @param {Map<string, { id: string; fileUrl: string; previewUrl: string; filename: string; uploadPropertyKey: string; uploadPropertyValue: string; firstProduct: string; setupVariantId: string }>} artworks
   */
  function renderReusableArtworks(root, artworks) {
    root._kitwearReusableArtworks = artworks;
    const choice = root.querySelector('[data-bulk-sponsor-reuse-choice]');
    const wrap = root.querySelector('[data-bulk-logo-reuse-wrap]');
    const options = root.querySelector('[data-bulk-logo-reuse-options]');
    if (!(choice instanceof HTMLElement) || !(wrap instanceof HTMLElement) || !(options instanceof HTMLElement)) {
      return;
    }
    const selected = options.querySelector('input[data-bulk-logo-reuse-input]:checked');
    const previous = selected instanceof HTMLInputElement ? selected.value : '';
    const radioName = options.getAttribute('data-radio-name') || `bulk-logo-reuse-${Date.now()}`;
    options.innerHTML = '';
    let artworkIndex = 0;
    artworks.forEach((artwork) => {
      artworkIndex += 1;
      const label = document.createElement('label');
      label.className = 'BulkOrderTable-logoReuseChoice';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = radioName;
      input.value = artwork.id;
      input.className = 'visually-hidden';
      input.setAttribute('data-bulk-logo-reuse-input', '');
      input.setAttribute('aria-label', `Uploaded logo ${artworkIndex}`);
      if (previous === artwork.id) input.checked = true;

      const tile = document.createElement('span');
      tile.className = 'BulkOrderTable-logoReuseTile';
      const fallback = document.createElement('span');
      fallback.className = 'BulkOrderTable-logoReuseFallback';
      fallback.textContent = 'Uploaded logo';
      fallback.hidden = Boolean(artwork.previewUrl);
      if (artwork.previewUrl) {
        const image = document.createElement('img');
        image.className = 'BulkOrderTable-logoReuseImage';
        image.src = artwork.previewUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.setAttribute('aria-hidden', 'true');
        image.addEventListener('error', () => {
          image.removeAttribute('src');
          image.hidden = true;
          fallback.hidden = false;
          tile.classList.add('BulkOrderTable-logoReuseTile--fallback');
        });
        tile.append(image);
      } else {
        tile.classList.add('BulkOrderTable-logoReuseTile--fallback');
      }
      const check = document.createElement('span');
      check.className = 'BulkOrderTable-logoReuseCheck';
      check.textContent = '✓';
      check.setAttribute('aria-hidden', 'true');
      tile.append(fallback, check);
      label.append(input, tile);
      options.appendChild(label);
    });
    const hasArtworks = artworks.size > 0 && isEligibleArtworkKitMode(root);
    choice.hidden = !hasArtworks;
    options.querySelectorAll('input[data-bulk-logo-reuse-input]').forEach((input) => {
      input.disabled = !hasArtworks;
    });
    const reuseChecked = Boolean(
      root.querySelector('input[data-bulk-sponsor-radio][value="reuse"]:checked')
    );
    wrap.hidden = !hasArtworks || !reuseChecked;
  }

  /**
   * @param {HTMLElement} root
   */
  async function refreshReusableArtworks(root) {
    if (!isEligibleArtworkKitMode(root)) {
      renderReusableArtworks(root, new Map());
      return;
    }
    const cart = await fetchCartJson();
    renderReusableArtworks(root, reusableArtworksFromCart(cart || { items: [] }));
  }

  /**
   * @param {HTMLElement} root
   */
  function selectedReusableArtwork(root) {
    const selected = root.querySelector('input[data-bulk-logo-reuse-input]:checked');
    const artworks = root._kitwearReusableArtworks;
    if (!(selected instanceof HTMLInputElement) || !(artworks instanceof Map)) {
      return null;
    }
    return artworks.get(selected.value) || null;
  }

  /**
   * Resolve a genuinely new upload. SHA-256 is calculated only from original File bytes.
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} mainForm
   */
  async function resolveNewArtworkMetadata(root, mainForm) {
    const form = resolveMainForm(root, mainForm);
    const capture = originalArtworkFileCapture(root, form);
    artworkDebug('Captured File present', {
      capturedFilePresent: Boolean(capture && capture.file instanceof File),
      filename: capture && capture.file instanceof File ? capture.file.name : '',
    });
    if (!capture) {
      artworkDebug('Add-to-cart validation failed: original File object was not captured');
      throw new Error(
        root.getAttribute('data-msg-artwork-file-unavailable') ||
          'Please upload your new sponsor logo and wait for the upload to finish before adding this item to your basket.'
      );
    }
    let id = '';
    try {
      artworkDebug('SHA readiness before validation', { shaReady: /^[a-f0-9]{64}$/.test(capture.hash) });
      id = capture.hash || (await capture.fingerprint);
    } catch (error) {
      artworkDebug('Add-to-cart validation failed: file-byte SHA-256 was unavailable', {
        reason: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
    if (!/^[a-f0-9]{64}$/.test(id)) {
      artworkDebug('Add-to-cart validation failed: SHA-256 result was invalid');
      throw new Error(root.getAttribute('data-msg-artwork-file-unavailable') || 'Could not identify the artwork file.');
    }
    artworkDebug('SHA readiness after validation', { shaReady: true });
    const currentUploadProperty = await waitForHumanUploadProperty(root, form);
    if (!currentUploadProperty) {
      throw new Error(root.getAttribute('data-msg-sponsor-upload-required') || 'Wait for the logo upload to finish.');
    }
    const runtimePreviewUrl = uploadlyRuntimePreviewUrl(root, form, currentUploadProperty);
    if (runtimePreviewUrl) {
      rememberArtworkPreviewUrl(id, runtimePreviewUrl);
    }
    const cart = await fetchCartJson();
    const duplicate = reusableArtworksFromCart(cart || { items: [] }).get(id) || null;
    const setupVariantId = String(root.getAttribute('data-bulk-setup-fee-variant-id') || '').trim();
    artworkDebug('New upload metadata ready', {
      propertyKey: currentUploadProperty.key,
      propertyValue: currentUploadProperty.value,
      previewAvailable: Boolean(runtimePreviewUrl),
      uploadConsideredReady: true,
    });
    return {
      id,
      fileUrl: duplicate ? duplicate.fileUrl : currentUploadProperty.fileUrl,
      filename: duplicate ? duplicate.filename : capture.file.name,
      uploadPropertyKey: duplicate ? duplicate.uploadPropertyKey : currentUploadProperty.key,
      uploadPropertyValue: duplicate ? duplicate.uploadPropertyValue : currentUploadProperty.value,
      setupVariantId: duplicate && duplicate.setupVariantId ? duplicate.setupVariantId : setupVariantId,
    };
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} mainForm
   * @returns {HTMLFormElement | null}
   */
  function resolveMainForm(root, mainForm) {
    if (mainForm instanceof HTMLFormElement) {
      return mainForm;
    }
    const wrapper = root.closest('.product-form');
    const f = wrapper && wrapper.querySelector('form[is="product-form"]');
    if (f instanceof HTMLFormElement) {
      return f;
    }
    const fallback = document.querySelector('form[is="product-form"]');
    return fallback instanceof HTMLFormElement ? fallback : null;
  }

  /**
   * New logo: require a chosen file and/or a line-item property URL from the product form (Uploadly / apps).
   * @param {HTMLFormElement | null} form
   */
  function hasNewLogoUploadEvidence(form, root) {
    const scope =
      (root instanceof HTMLElement && root.closest('.product-form')) ||
      (form instanceof HTMLFormElement && form.closest('.product-form')) ||
      form;
    const scopeEl = scope instanceof HTMLElement ? scope : null;

    if (!form) {
      if (!scopeEl) {
        return false;
      }
    }
    const fileInputs = (scopeEl || form).querySelectorAll('input[type="file"]');
    for (let i = 0; i < fileInputs.length; i++) {
      const fi = fileInputs[i];
      if (fi instanceof HTMLInputElement && fi.files && fi.files.length > 0) {
        return true;
      }
    }
    if (form) {
      const fd = new FormData(form);
      for (const [key, value] of fd.entries()) {
        if (typeof value !== 'string') {
          continue;
        }
        const m = key.match(/^properties\[(.+)\]$/);
        if (!m) {
          continue;
        }
        const subKey = m[1];
        const v = String(value).trim();
        if (!v) {
          continue;
        }
        const sk = subKey.toLowerCase();
        if (sk === 'logo position') {
          continue;
        }
        if (sk === 'sponsor logo' && !looksLikeUploadPropertyUrl(v)) {
          continue;
        }
        if (looksLikeUploadPropertyUrl(v)) {
          return true;
        }
      }
      // Some upload apps do not populate `properties[...]` until submit; check any form field value for upload URLs.
      for (const [, value] of fd.entries()) {
        if (typeof value !== 'string') {
          continue;
        }
        const v = String(value).trim();
        if (!v) {
          continue;
        }
        if (looksLikeUploadPropertyUrl(v)) {
          return true;
        }
      }
    }

    // Upload apps may render inputs outside the main product form.
    if (scopeEl) {
      const auxValues = scopeEl.querySelectorAll('input, textarea, select');
      for (let i = 0; i < auxValues.length; i++) {
        const el = auxValues[i];
        if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
          continue;
        }
        const v = String('value' in el ? el.value : '').trim();
        if (!v) {
          continue;
        }
        if (looksLikeUploadPropertyUrl(v)) {
          return true;
        }
      }
    }

    // Last fallback: uploaded-file preview/image evidence inside the product form.
    const previewImg = (scopeEl || form).querySelector(
      'img[src*="ucarecdn.com"], img[src*="uploadcare.com"], img[src*="uploadly"], img[src*="com-uploadly.com"], img[src*="/files/"]'
    );
    if (previewImg instanceof HTMLImageElement) {
      return true;
    }

    return false;
  }

  /**
   * Optional global properties inside bulk root (e.g. hidden fields).
   * @param {HTMLElement} root
   */
  function collectGlobalPropsFromRoot(root) {
    const props = {};
    root.querySelectorAll('[data-bulk-global-prop]').forEach((el) => {
      const key = el.getAttribute('data-bulk-global-prop');
      if (!key || !('value' in el)) {
        return;
      }
      const input = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (el);
      if (input.disabled) {
        return;
      }
      if (input.type === 'file') {
        return;
      }
      const v = input.value.trim();
      if (v) {
        props[key] = v;
      }
    });
    return props;
  }

  /**
   * @param {HTMLElement} container
   */
  function collectLinePropsFromLineItem(container) {
    const props = {};
    container.querySelectorAll('[data-bulk-line-prop]').forEach((el) => {
      const key = el.getAttribute('data-bulk-line-prop');
      if (!key || !('value' in el)) {
        return;
      }
      const input = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (el);
      if (input.disabled) {
        return;
      }
      const v = input.value.trim();
      if (v) {
        props[key] = normalizeLinePropertyValue(key, v);
      }
    });
    return props;
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  function normalizeLinePropertyValue(key, value) {
    const k = String(key || '').trim().toLowerCase();
    const v = String(value || '');
    if (k === 'initials') {
      return v.toUpperCase();
    }
    return v;
  }

  /**
   * @param {Record<string, string>} obj
   */
  function cleanMergedProps(obj) {
    /** @type {Record<string, string>} */
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (isUploadlyNotesPropertyKey(k)) {
        return;
      }
      if (v == null) {
        return;
      }
      const trimmed = normalizeLinePropertyValue(k, String(v)).trim();
      if (!trimmed) {
        return;
      }
      if (trimmed.length > LINE_ITEM_PROPERTY_MAX_LEN) {
        return;
      }
      out[k] = trimmed;
    });
    return out;
  }

  /**
   * @param {unknown} raw
   * @returns {Record<string, string>}
   */
  function normalizeLineItemProperties(raw) {
    if (!raw || typeof raw !== 'object') {
      return {};
    }
    if (Array.isArray(raw)) {
      /** @type {Record<string, string>} */
      const out = {};
      raw.forEach((row) => {
        if (!row || typeof row !== 'object' || !('name' in row)) {
          return;
        }
        const entry = /** @type {{ name?: unknown; value?: unknown }} */ (row);
        const k = entry.name != null ? String(entry.name) : '';
        const v = entry.value != null ? String(entry.value) : '';
        if (k) {
          out[k] = v;
        }
      });
      return out;
    }
    /** @type {Record<string, string>} */
    const out = {};
    Object.keys(/** @type {Record<string, unknown>} */ (raw)).forEach((k) => {
      const v = /** @type {Record<string, unknown>} */ (raw)[k];
      out[k] = v != null ? String(v) : '';
    });
    return out;
  }

  /**
   * @param {unknown} value
   */
  function normalizeTeamNumber(value) {
    return String(value != null ? value : '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  /**
   * @param {HTMLElement} root
   */
  function isPersonalizeActive(root) {
    return root.getAttribute('data-bulk-personalize-active') === 'true';
  }

  /**
   * Team kit sponsor panel OR workwear logo-setup checkbox.
   * Both drive `data-bulk-fee-required` so each add gets an attributed fee line when new logo setup is needed.
   * @param {HTMLElement} root
   */
  function initSponsorPanel(root) {
    /**
     * @param {string} s
     */
    function setupChoiceLabelFromVariantTitle(s) {
      return String(s || '')
        .replace(/^setup\s*-\s*/i, '')
        .trim();
    }

    const panel = root.querySelector('[data-bulk-sponsor-panel]');
    const workwearFeePanel = root.querySelector('[data-bulk-logo-fee-panel]');
    const feeVariantPoolScript = root.querySelector('[data-bulk-fee-variant-pool]');
    const feeSetupValueBase =
      root.getAttribute('data-bulk-fee-setup-value-base') ||
      root.getAttribute('data-bulk-fee-setup-value') ||
      'Logo setup (per order)';
    const bulkKitMode = (root.getAttribute('data-bulk-kit-mode') || '').trim().toLowerCase();
    const existingVal = root.getAttribute('data-sponsor-existing-value') || '';
    const newVal = root.getAttribute('data-sponsor-new-value') || '';
    const reuseVal = root.getAttribute('data-sponsor-reuse-value') || newVal;
    /**
     * @param {Element | null} script
     * @returns {Array<{ id: string; title: string; priceCents: number }>}
     */
    function parseVariantPool(script) {
      if (!script || !script.textContent) {
        return [];
      }
      try {
        const parsed = JSON.parse(script.textContent.trim());
        if (!Array.isArray(parsed)) {
          return [];
        }
        return parsed
          .map((row) => {
            if (!row || typeof row !== 'object') {
              return null;
            }
            const r = /** @type {{ id?: unknown; title?: unknown; priceCents?: unknown }} */ (row);
            const id = String(r.id != null ? r.id : '').trim();
            const title = String(r.title != null ? r.title : '').trim();
            const priceCents = parseInt(String(r.priceCents != null ? r.priceCents : '0'), 10);
            if (!id || !title) {
              return null;
            }
            return { id, title, priceCents: Number.isNaN(priceCents) ? 0 : priceCents };
          })
          .filter(Boolean);
      } catch {
        return [];
      }
    }

    const feeVariantPool = parseVariantPool(feeVariantPoolScript);

    /**
     * Logo setup options from shop metafield custom.bulk_logo_setup_fee_variants only.
     * @returns {Array<{ label: string; variantId: string; setupValue: string; priceCents: number }>}
     */
    function parseFeeChoices() {
      /** @type {Array<{ label: string; variantId: string; setupValue: string; priceCents: number }>} */
      const out = [];
      feeVariantPool.forEach((row) => {
        const label = setupChoiceLabelFromVariantTitle(row.title) || row.title;
        if (!label || !row.id) {
          return;
        }
        out.push({
          label,
          variantId: row.id,
          setupValue: '',
          priceCents: row.priceCents || 0,
        });
      });
      return out;
    }

    const feeChoices = parseFeeChoices();
    const visibleFeeChoices =
      bulkKitMode === 'none'
        ? feeChoices.filter((choice) => !/initial/i.test(String(choice.label || '')))
        : feeChoices;

    // Sponsor positions are free. Resolve the existing £15 new-logo setup variant
    // automatically so customers no longer choose a print/embroidery setup type.
    const automaticSetupChoice =
      visibleFeeChoices.find(
        (choice) => choice.priceCents === 1500 && /new\s*logo/i.test(choice.label)
      ) ||
      visibleFeeChoices.find(
        (choice) => choice.priceCents === 1500 && !/initial/i.test(choice.label)
      ) ||
      visibleFeeChoices.find((choice) => choice.priceCents === 1500) ||
      null;

    /**
     * @returns {{ label: string; variantId: string; setupValue: string; priceCents: number } | null}
     */
    function selectedFeeChoice() {
      return automaticSetupChoice;
    }

    function applyFeeChoiceToRoot() {
      const selected = selectedFeeChoice();
      const variantId = selected && selected.variantId ? selected.variantId : '';
      root.setAttribute('data-bulk-setup-fee-variant-id', variantId);
      root.setAttribute('data-bulk-setup-fee-price-cents', String(selected?.priceCents || 0));
      if (selected && selected.setupValue) {
        root.setAttribute('data-bulk-fee-setup-value', selected.setupValue);
      } else if (selected && selected.label) {
        root.setAttribute('data-bulk-fee-setup-value', `${feeSetupValueBase} (${selected.label})`);
      } else {
        root.setAttribute('data-bulk-fee-setup-value', feeSetupValueBase);
      }
      return selected;
    }

    /**
     * @param {boolean} isRequired
     */
    function setFeeRequired(isRequired) {
      const feeId = (root.getAttribute('data-bulk-setup-fee-variant-id') || '').trim();
      root.setAttribute('data-bulk-fee-required', isRequired && Boolean(feeId) ? 'true' : 'false');
    }

    if (!panel && !workwearFeePanel) {
      setFeeRequired(false);
      return;
    }

    if (!panel && workwearFeePanel) {
      const feeCheckbox = workwearFeePanel.querySelector('[data-bulk-logo-fee-cb]');
      const feeHidden = workwearFeePanel.querySelector('[data-bulk-logo-fee-hidden]');
      const logoHidden = workwearFeePanel.querySelector('[data-bulk-logo-position-hidden]');
      const logoWrap = workwearFeePanel.querySelector('[data-bulk-logo-position-wrap]');
      function logoPositionsSummaryWorkwear() {
        const checked = workwearFeePanel.querySelectorAll('[data-bulk-logo-position-cb]:checked');
        const parts = [];
        checked.forEach((cb) => {
          const v = /** @type {HTMLInputElement} */ (cb).value.trim();
          if (v) {
            parts.push(v);
          }
        });
        return parts.join('; ');
      }
      const syncWorkwear = () => {
        const isNew = Boolean(feeCheckbox instanceof HTMLInputElement && feeCheckbox.checked);
        applyFeeChoiceToRoot();
        if (feeHidden instanceof HTMLInputElement) {
          feeHidden.value = isNew ? newVal : '';
        }
        if (logoWrap instanceof HTMLElement) {
          logoWrap.hidden = !isNew;
        }
        if (logoHidden instanceof HTMLInputElement) {
          logoHidden.value = isNew ? logoPositionsSummaryWorkwear() : '';
        }
        setFeeRequired(isNew);
        syncLogoPositionsForSetup(root, workwearFeePanel);
      };
      workwearFeePanel.addEventListener('change', (e) => {
        const t = /** @type {HTMLElement} */ (e.target);
        if (t.matches('[data-bulk-logo-fee-cb], [data-bulk-logo-position-cb]')) {
          syncWorkwear();
        }
      });
      syncWorkwear();
      return;
    }

    if (!panel) {
      setFeeRequired(false);
      return;
    }

    const sponsorHidden = panel.querySelector('[data-bulk-sponsor-hidden]');
    const logoHidden = panel.querySelector('[data-bulk-logo-position-hidden]');
    const wrap = panel.querySelector('[data-bulk-logo-position-wrap]');
    const sponsorNameWrap = panel.querySelector('[data-bulk-sponsor-name-wrap]');
    const sponsorNameInput = panel.querySelector('[data-bulk-sponsor-name]');
    const reuseWrap = panel.querySelector('[data-bulk-logo-reuse-wrap]');

    function logoPositionsSummary() {
      const checked = panel.querySelectorAll('[data-bulk-logo-position-cb]:checked');
      const parts = [];
      checked.forEach((cb) => {
        const v = /** @type {HTMLInputElement} */ (cb).value.trim();
        if (v) {
          parts.push(v);
        }
      });
      return parts.join('; ');
    }

    function sync() {
      const checkedRadio = panel.querySelector('input[data-bulk-sponsor-radio]:checked');
      const mode =
        checkedRadio instanceof HTMLInputElement ? String(checkedRadio.value || '').trim() : '';
      const isNew = mode === 'new';
      const isExisting = mode === 'existing';
      const isReuse = mode === 'reuse';
      const hasSelection = isNew || isExisting || isReuse;
      applyFeeChoiceToRoot();
      if (sponsorHidden) {
        sponsorHidden.value = hasSelection ? (isNew ? newVal : isReuse ? reuseVal : existingVal) : '';
      }
      if (wrap) {
        wrap.hidden = !hasSelection;
      }
      if (logoHidden) {
        logoHidden.value = hasSelection ? logoPositionsSummary() : '';
      }
      if (sponsorNameWrap instanceof HTMLElement) {
        sponsorNameWrap.hidden = !isExisting;
      }
      if (sponsorNameInput instanceof HTMLInputElement) {
        sponsorNameInput.disabled = !isExisting;
        if (!isExisting) {
          sponsorNameInput.value = '';
        }
      }
      if (reuseWrap instanceof HTMLElement) {
        const reusable = root._kitwearReusableArtworks;
        reuseWrap.hidden = !isReuse || !(reusable instanceof Map) || reusable.size === 0;
      }
      setFeeRequired(isNew);
      syncLogoPositionsForSetup(root, panel);
    }

    panel.addEventListener('change', (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      if (t.matches('[data-bulk-sponsor-radio], [data-bulk-logo-position-cb]')) {
        sync();
      }
    });
    sync();
  }

  /**
   * Keep the required human-readable garment property in sync with the Default
   * Product radio choice. Neither option is selected on initial render.
   * @param {HTMLElement} root
   */
  function initLogoApplicationMethod(root) {
    if (!isDefaultApplicationProduct(root)) {
      return;
    }
    const hidden = root.querySelector('[data-bulk-logo-application-hidden]');
    const sync = () => {
      const selected = root.querySelector('[data-bulk-logo-application-method]:checked');
      if (hidden instanceof HTMLInputElement) {
        hidden.value = selected instanceof HTMLInputElement ? selected.value : '';
      }
    };
    root.querySelectorAll('[data-bulk-logo-application-method]').forEach((radio) => {
      radio.addEventListener('change', sync);
    });
    sync();
  }

  /**
   * @param {HTMLInputElement[]} inputs
   * @param {HTMLFormElement | null} mainForm
   * @param {HTMLElement} root
   */
  function buildCartItems(inputs, mainForm, root, artworkMetadata = null) {
    // Existing sponsor / no new logo: use bulk hidden fields only — not Uploadly fields on the product form.
    const globalForm = isNewLogoChosen(root)
      ? collectArtworkUploadProperties(root, resolveMainForm(root, mainForm))
      : {};
    const globalRoot = collectGlobalPropsFromRoot(root);
    const base = { ...globalRoot, ...globalForm };

    if (artworkMetadata && isNewArtworkChosen(root)) {
      base[ARTWORK_ID_KEY] = String(artworkMetadata.id || '');
      base[ARTWORK_FILENAME_KEY] = String(artworkMetadata.filename || '').slice(
        0,
        LINE_ITEM_PROPERTY_MAX_LEN
      );
      base[ARTWORK_SETUP_VARIANT_KEY] = String(artworkMetadata.setupVariantId || '');
      if (artworkMetadata.uploadPropertyKey && artworkMetadata.uploadPropertyValue) {
        // Preserve Uploadly's human-readable/clickable property for both new and reused garments.
        base[artworkMetadata.uploadPropertyKey] = artworkMetadata.uploadPropertyValue;
      }
    }

    if (!isPersonalizeActive(root)) {
      const items = [];
      for (const qtyInput of inputs) {
        if (qtyInput.disabled) {
          continue;
        }
        const vid = qtyInput.getAttribute('data-variant-id');
        const qty = parseInt(qtyInput.value, 10) || 0;
        if (!vid || qty < 1) {
          continue;
        }
        const cleaned = cleanMergedProps({ ...base });
        const item = { id: Number(vid), quantity: qty };
        if (Object.keys(cleaned).length > 0) {
          item.properties = cleaned;
        }
        items.push(item);
      }
      return items;
    }

    const list = root.querySelector('[data-bulk-personalize-list]');
    if (!list) {
      return [];
    }
    const units = list.querySelectorAll('[data-bulk-personalize-unit]');
    if (!units.length) {
      return [];
    }

    const items = [];
    units.forEach((unitEl) => {
      const vid = unitEl.getAttribute('data-variant-id');
      if (!vid) {
        return;
      }
      const lineProps = collectLinePropsFromLineItem(unitEl);
      const merged = { ...base, ...lineProps };
      const cleaned = cleanMergedProps(merged);
      const item = { id: Number(vid), quantity: 1 };
      if (Object.keys(cleaned).length > 0) {
        item.properties = cleaned;
      }
      items.push(item);
    });
    return items;
  }

  /** Shopify line item property values are limited (~255 chars); split across numbered keys if needed. */
  const FEE_APPLIES_MAX_LEN = 250;

  /**
   * Line item properties on the logo setup fee variant: what kit lines this charge belongs to.
   * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
   * @param {HTMLInputElement[]} inputs
   * @param {HTMLElement} root
   * @returns {Record<string, string>}
   */
  function buildFeeLineProperties(items, inputs, root) {
    const setupVal = (root.getAttribute('data-bulk-fee-setup-value') || 'Logo setup (per order)').trim();
    const appliesKey = (root.getAttribute('data-bulk-fee-applies-key') || 'Applies to').trim();
    const productTitle = (root.getAttribute('data-bulk-product-title') || '').trim() || 'Kit order';

    const colorLabel = (() => {
      const sel = root.querySelector('[data-bulk-color-select]');
      if (sel instanceof HTMLSelectElement && sel.options && sel.options.length) {
        const opt = sel.options[sel.selectedIndex];
        return opt ? opt.text.trim() : '';
      }
      return '';
    })();

    /** @type {Map<string, number>} */
    const byVid = new Map();
    for (const it of items) {
      const id = String(it.id);
      byVid.set(id, (byVid.get(id) || 0) + (it.quantity || 0));
    }

    const inputList = Array.from(inputs);
    const lines = [];
    for (const [vid, qty] of byVid.entries()) {
      const inp = inputList.find((i) => i.getAttribute('data-variant-id') === vid && !i.disabled);
      const size = inp ? (inp.getAttribute('data-size') || '').trim() : '';
      lines.push(size ? `${size} × ${qty}` : `Variant ${vid} × ${qty}`);
    }

    let summary = productTitle;
    if (colorLabel) {
      summary += ` · ${colorLabel}`;
    }
    if (lines.length) {
      summary += ` · ${lines.join('; ')}`;
    }

    /** @type {Record<string, string>} */
    const props = {
      Setup: setupVal,
    };

    if (!summary) {
      return props;
    }

    if (summary.length <= FEE_APPLIES_MAX_LEN) {
      props[appliesKey] = summary;
      return props;
    }

    let offset = 0;
    let part = 0;
    while (offset < summary.length && part < 8) {
      const chunk = summary.slice(offset, offset + FEE_APPLIES_MAX_LEN);
      const key = part === 0 ? appliesKey : `${appliesKey} (${part + 1})`;
      props[key] = chunk;
      offset += FEE_APPLIES_MAX_LEN;
      part += 1;
    }
    return props;
  }

  /**
   * @param {string} s
   */
  function normalizeChoiceLabel(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  const LARGE_POSITION_KEYS = new Set(['large_chest', 'large_back', 'big_back']);
  const TRAINING_WEAR_LARGE_POSITION_KEYS = new Set(['large_chest', 'large_back']);
  const TRAINING_WEAR_SMALL_POSITION_KEYS = new Set(['left_sleeve', 'right_sleeve']);

  /** @type {WeakMap<HTMLElement, { printLarge: number[]; printSmall: number[]; embroideryLarge: number[]; embroiderySmall: number[] } | null>} */
  const tierIdsCache = new WeakMap();
  /** @type {WeakMap<HTMLElement, { printLarge: number; printSmall: number; personalisation: number; personalisationAvailable: boolean } | null>} */
  const trainingWearAppIdsCache = new WeakMap();

  /**
   * @param {HTMLElement} root
   * @returns {{ printLarge: number[]; printSmall: number[]; embroideryLarge: number[]; embroiderySmall: number[] } | null}
   */
  function readAppTierIds(root) {
    if (tierIdsCache.has(root)) {
      return tierIdsCache.get(root) || null;
    }
    const script = root.querySelector('[data-kitwear-app-tier-ids]');
    /** @type {{ printLarge: number[]; printSmall: number[]; embroideryLarge: number[]; embroiderySmall: number[] } | null} */
    let parsed = null;
    if (script && script.textContent) {
      try {
        const data = JSON.parse(script.textContent.trim());
        if (data && typeof data === 'object') {
          const norm = (arr) =>
            Array.isArray(arr)
              ? arr.map((x) => parseInt(String(x), 10)).filter((id) => !Number.isNaN(id) && id > 0)
              : [];
          parsed = {
            printLarge: norm(/** @type {unknown[]} */ (data.printLarge)),
            printSmall: norm(/** @type {unknown[]} */ (data.printSmall)),
            embroideryLarge: norm(/** @type {unknown[]} */ (data.embroideryLarge)),
            embroiderySmall: norm(/** @type {unknown[]} */ (data.embroiderySmall)),
          };
        }
      } catch {
        parsed = null;
      }
    }
    tierIdsCache.set(root, parsed);
    return parsed;
  }

  /**
   * Read the fixed Training Wear print and personalisation variants. These are single
   * variant references, not tier lists, so quantity never selects a different variant.
   * @param {HTMLElement} root
   * @returns {{ printLarge: number; printSmall: number; personalisation: number; personalisationAvailable: boolean } | null}
   */
  function readTrainingWearAppIds(root) {
    if (trainingWearAppIdsCache.has(root)) {
      return trainingWearAppIdsCache.get(root) || null;
    }
    const script = root.querySelector('[data-kitwear-training-wear-app-ids]');
    /** @type {{ printLarge: number; printSmall: number; personalisation: number; personalisationAvailable: boolean } | null} */
    let parsed = null;
    if (script && script.textContent) {
      try {
        const data = JSON.parse(script.textContent.trim());
        const printLarge = parseInt(String(data && data.printLarge), 10);
        const printSmall = parseInt(String(data && data.printSmall), 10);
        const personalisation = parseInt(String(data && data.personalisation), 10);
        parsed = {
          printLarge: printLarge > 0 ? printLarge : 0,
          printSmall: printSmall > 0 ? printSmall : 0,
          personalisation: personalisation > 0 ? personalisation : 0,
          personalisationAvailable: Boolean(data && data.personalisationAvailable),
        };
      } catch {
        parsed = null;
      }
    }
    trainingWearAppIdsCache.set(root, parsed);
    return parsed;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} attr
   * @returns {number[]}
   */
  function parseTierVariantCsv(root, attr) {
    const tiers = readAppTierIds(root);
    if (tiers) {
      if (attr === 'data-bulk-app-tier-print-large') {
        return tiers.printLarge;
      }
      if (attr === 'data-bulk-app-tier-print-small') {
        return tiers.printSmall;
      }
      if (attr === 'data-bulk-app-tier-embroidery-large') {
        return tiers.embroideryLarge;
      }
      if (attr === 'data-bulk-app-tier-embroidery-small') {
        return tiers.embroiderySmall;
      }
    }
    const raw = root.getAttribute(attr) || '';
    if (!raw.trim()) {
      return [];
    }
    return raw
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }

  /**
   * @param {number} applicationCount
   * @returns {number}
   */
  function applicationTierIndex(applicationCount) {
    const n = applicationCount || 0;
    if (n <= 0) {
      return -1;
    }
    if (n <= 9) {
      return 0;
    }
    if (n <= 24) {
      return 1;
    }
    if (n <= 49) {
      return 2;
    }
    if (n <= 74) {
      return 3;
    }
    if (n <= 99) {
      return 4;
    }
    if (n <= 249) {
      return 5;
    }
    return 6;
  }

  /**
   * @param {number} cents
   */
  function formatMoneyFromCents(cents) {
    const format = window.themeVariables?.settings?.moneyFormat;
    const amount = (cents / 100).toFixed(2);
    if (format && typeof format === 'string') {
      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }
    return `£${amount}`;
  }

  /**
   * @param {HTMLElement} root
   * @returns {Array<{ min: number; percent: number }>}
   */
  function readGarmentVolumeTiers(root) {
    const script = root.querySelector('[data-bulk-garment-volume-tiers]');
    if (!script || !script.textContent) {
      return [];
    }
    try {
      const parsed = JSON.parse(script.textContent.trim());
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .map((row) => ({
          min: parseInt(String(row.min), 10),
          percent: parseInt(String(row.percent), 10),
        }))
        .filter((row) => !Number.isNaN(row.min) && row.min > 0 && !Number.isNaN(row.percent));
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLInputElement[]} inputs
   */
  function totalGarmentQtyFromInputs(inputs) {
    let total = 0;
    inputs.forEach((inp) => {
      if (inp.disabled) {
        return;
      }
      const q = parseInt(inp.value, 10) || 0;
      if (q > 0) {
        total += q;
      }
    });
    return total;
  }

  /**
   * @param {HTMLInputElement[]} inputs
   */
  function totalGarmentSubtotalCentsFromInputs(inputs) {
    let total = 0;
    inputs.forEach((inp) => {
      if (inp.disabled) {
        return;
      }
      const qty = parseInt(inp.value, 10) || 0;
      const unitCents = parseInt(inp.getAttribute('data-variant-price-cents') || '0', 10) || 0;
      if (qty > 0 && unitCents > 0) {
        total += qty * unitCents;
      }
    });
    return total;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLInputElement[]} inputs
   */
  function initGarmentVolumePricing(root, inputs) {
    const wrap = root.querySelector('[data-bulk-garment-volume]');
    if (!wrap) {
      return;
    }
    const tiers = readGarmentVolumeTiers(root);

    const update = () => {
      const qty = totalGarmentQtyFromInputs(inputs);
      wrap.querySelectorAll('[data-bulk-garment-tier]').forEach((row) => {
        row.classList.remove('BulkGarmentVolume__row--active');
        const min = parseInt(row.getAttribute('data-min') || '0', 10);
        const pct = parseInt(row.getAttribute('data-percent') || '0', 10);
        let active = false;
        if (qty < 1) {
          active = min === 1 && pct === 0;
        } else if (tiers.length) {
          const applicable = tiers.filter((t) => qty >= t.min);
          const best = applicable.length ? applicable[applicable.length - 1] : null;
          active = best ? best.min === min && best.percent === pct : min === 1 && pct === 0;
        } else {
          active = min === 1 && pct === 0;
        }
        if (active) {
          row.classList.add('BulkGarmentVolume__row--active');
        }
      });
    };

    inputs.forEach((inp) => {
      inp.addEventListener('input', update);
      inp.addEventListener('change', update);
    });
    update();
  }

  /**
   * @param {HTMLElement} root
   * @returns {{ printLarge: number[]; printSmall: number[]; embroideryLarge: number[]; embroiderySmall: number[] } | null}
   */
  function readAppTierPrices(root) {
    const script = root.querySelector('[data-bulk-app-tier-prices]');
    if (!script || !script.textContent) {
      return null;
    }
    try {
      const parsed = JSON.parse(script.textContent.trim());
      const mapPrices = (key) =>
        Array.isArray(parsed[key]) ? parsed[key].map((n) => Number(n)) : [];
      return {
        printLarge: mapPrices('printLarge'),
        printSmall: mapPrices('printSmall'),
        embroideryLarge: mapPrices('embroideryLarge'),
        embroiderySmall: mapPrices('embroiderySmall'),
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {number} garmentQty
   * @param {Array<{ key: string; label: string }>} entries
   */
  function estimateApplicationFeesCents(root, garmentQty, entries) {
    const prices = readAppTierPrices(root);
    if (!prices || garmentQty < 1 || !entries.length) {
      return null;
    }
    const tierIdx = applicationTierIndex(garmentQty);
    if (tierIdx < 0) {
      return null;
    }
    const embroidery = isEmbroideryApplicationMethod(root);
    let total = 0;
    for (const entry of entries) {
      const large = LARGE_POSITION_KEYS.has(entry.key);
      const bucket = embroidery
        ? large
          ? prices.embroideryLarge
          : prices.embroiderySmall
        : large
          ? prices.printLarge
          : prices.printSmall;
      const unitPrice = bucket[tierIdx];
      if (bucket.length < 7 || !unitPrice) {
        return null;
      }
      // Every position uses the tier selected from garmentQty, then charges garmentQty applications.
      total += garmentQty * unitPrice;
    }
    return total;
  }

  /**
   * Count the populated per-garment Training Wear fields currently rendered in Player Details.
   * @param {HTMLElement} root
   * @returns {{ initials: number; smallNumber: number }}
   */
  function populatedTrainingWearPersonalisationCountsFromRoot(root) {
    const counts = { initials: 0, smallNumber: 0 };
    root.querySelectorAll('[data-bulk-personalize-unit]').forEach((unit) => {
      const initials = unit.querySelector('[data-bulk-line-prop="Initials"]');
      const smallNumber = unit.querySelector('[data-bulk-line-prop="Small Number"]');
      if (initials instanceof HTMLInputElement && initials.value.trim()) {
        counts.initials += 1;
      }
      if (smallNumber instanceof HTMLInputElement && smallNumber.value.trim()) {
        counts.smallNumber += 1;
      }
    });
    return counts;
  }

  /**
   * Training Wear uses the current prices of its fixed variant references. Quantity
   * determines only how many applications are charged; it never selects a pricing tier.
   * @param {HTMLElement} root
   * @param {number} garmentQty
   * @param {Array<{ key: string; label: string }>} entries
   * @param {{ initials: number; smallNumber: number }} personalisationCounts
   * @returns {number | null}
   */
  function estimateTrainingWearApplicationFeesCents(root, garmentQty, entries, personalisationCounts) {
    const personalisationQty = personalisationCounts.initials + personalisationCounts.smallNumber;
    if (garmentQty < 1 || (!entries.length && personalisationQty < 1)) {
      return 0;
    }
    const script = root.querySelector('[data-kitwear-training-wear-app-ids]');
    if (!script || !script.textContent) {
      return null;
    }
    try {
      const configured = JSON.parse(script.textContent.trim());
      const largePrice = Number(configured && configured.printLargePriceCents);
      const smallPrice = Number(configured && configured.printSmallPriceCents);
      const personalisationPrice = Number(configured && configured.personalisationPriceCents);
      let total = 0;
      for (const entry of entries) {
        if (TRAINING_WEAR_LARGE_POSITION_KEYS.has(entry.key)) {
          if (!Number.isFinite(largePrice) || largePrice < 0) {
            return null;
          }
          total += garmentQty * largePrice;
        } else if (TRAINING_WEAR_SMALL_POSITION_KEYS.has(entry.key)) {
          if (!Number.isFinite(smallPrice) || smallPrice < 0) {
            return null;
          }
          total += garmentQty * smallPrice;
        }
      }
      if (personalisationQty > 0) {
        if (!Number.isFinite(personalisationPrice) || personalisationPrice < 0) {
          return null;
        }
        total += personalisationQty * personalisationPrice;
      }
      return total;
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLInputElement[]} inputs
   */
  function updateApplicationFeeEstimate(root, inputs) {
    const panel = root.querySelector('[data-bulk-app-estimate]');
    const line = root.querySelector('[data-bulk-app-estimate-line]');
    if (!(panel instanceof HTMLElement) || !(line instanceof HTMLElement)) {
      return;
    }
    if (!isLogoPricingSelectionActive(root)) {
      panel.hidden = true;
      return;
    }
    const entries = selectedPositionEntriesFromRoot(root);
    if (!entries.length) {
      panel.hidden = true;
      return;
    }
    const garmentQty = totalGarmentQtyFromInputs(inputs);
    if (garmentQty < 1) {
      panel.hidden = true;
      return;
    }
    const cents = estimateApplicationFeesCents(root, garmentQty, entries);
    if (cents == null) {
      panel.hidden = true;
      return;
    }
    const applicationCount = entries.length * garmentQty;
    const template =
      root.getAttribute('data-bulk-app-estimate-template') ||
      'Estimated application fees: {{ total }} ({{ count }} applications at current tier).';
    line.textContent = template
      .replace(/\{\{\s*total\s*\}\}/g, formatMoneyFromCents(cents))
      .replace(/\{\{\s*count\s*\}\}/g, String(applicationCount));
    panel.hidden = false;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLInputElement[]} inputs
   */
  function initApplicationFeeEstimate(root, inputs) {
    const refresh = () => updateApplicationFeeEstimate(root, inputs);
    inputs.forEach((inp) => {
      inp.addEventListener('input', refresh);
      inp.addEventListener('change', refresh);
    });
    root.querySelectorAll('[data-bulk-logo-position-cb]').forEach((cb) => {
      cb.addEventListener('change', refresh);
    });
    const feeSelect = root.querySelector('[data-bulk-fee-choice-select]');
    if (feeSelect) {
      feeSelect.addEventListener('change', refresh);
    }
    const feeCb = root.querySelector('[data-bulk-logo-fee-cb]');
    if (feeCb) {
      feeCb.addEventListener('change', refresh);
    }
    root.querySelectorAll('[data-bulk-sponsor-radio]').forEach((rb) => {
      rb.addEventListener('change', refresh);
    });
    refresh();
  }

  /**
   * @param {HTMLElement} root
   */
  function selectedSetupFeeCents(root) {
    if (!isNewLogoChosen(root)) {
      return 0;
    }
    return parseInt(root.getAttribute('data-bulk-setup-fee-price-cents') || '0', 10) || 0;
  }

  /**
   * @param {HTMLElement} root
   * @param {number} garmentQty
   */
  function selectedClubBadgeFeeCents(root, garmentQty) {
    if (!isClubBadgeEmbroideryChosen(root) || garmentQty < 1) {
      return 0;
    }
    const unit = parseInt(root.getAttribute('data-bulk-club-badge-embroidery-price-cents') || '0', 10) || 0;
    return unit > 0 ? unit * garmentQty : 0;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLInputElement[]} inputs
   */
  function updateOrderSummary(root, inputs) {
    const panel = root.querySelector('[data-bulk-order-summary]');
    if (!(panel instanceof HTMLElement)) {
      return;
    }
    const garmentsEl = panel.querySelector('[data-bulk-summary-garments]');
    const setupRow = panel.querySelector('[data-bulk-summary-setup-row]');
    const setupEl = panel.querySelector('[data-bulk-summary-setup]');
    const applicationRow = panel.querySelector('[data-bulk-summary-application-row]');
    const applicationEl = panel.querySelector('[data-bulk-summary-application]');
    const totalEl = panel.querySelector('[data-bulk-summary-total]');
    if (!(garmentsEl instanceof HTMLElement) || !(totalEl instanceof HTMLElement)) {
      return;
    }

    const garmentSubtotalCents = totalGarmentSubtotalCentsFromInputs(inputs);
    const setupCents = isNewLogoChosen(root) ? selectedSetupFeeCents(root) : 0;
    const garmentQty = totalGarmentQtyFromInputs(inputs);
    const methodSelected = Boolean(root.querySelector('[data-bulk-logo-application-method]:checked'));
    const selectedPositions = selectedPositionEntriesFromRoot(root);
    let applicationEstimate = 0;
    if (isDefaultApplicationProduct(root) && methodSelected) {
      applicationEstimate = estimateApplicationFeesCents(root, garmentQty, selectedPositions);
    } else if (isTrainingWearApplicationProduct(root)) {
      const personalisationCounts = populatedTrainingWearPersonalisationCountsFromRoot(root);
      applicationEstimate = estimateTrainingWearApplicationFeesCents(
        root,
        garmentQty,
        selectedPositions,
        personalisationCounts
      );
    }
    const applicationCents = applicationEstimate == null ? 0 : applicationEstimate;
    const totalCents = garmentSubtotalCents + setupCents + applicationCents;

    garmentsEl.textContent = formatMoneyFromCents(garmentSubtotalCents);
    if (setupRow instanceof HTMLElement && setupEl instanceof HTMLElement) {
      setupEl.textContent = formatMoneyFromCents(setupCents);
      setupRow.hidden = false;
    }
    if (applicationRow instanceof HTMLElement && applicationEl instanceof HTMLElement) {
      applicationEl.textContent = formatMoneyFromCents(applicationCents);
      applicationRow.hidden = false;
    }
    totalEl.textContent = formatMoneyFromCents(totalCents);
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLInputElement[]} inputs
   */
  function initOrderSummary(root, inputs) {
    const refresh = () => updateOrderSummary(root, inputs);
    inputs.forEach((inp) => {
      inp.addEventListener('input', refresh);
      inp.addEventListener('change', refresh);
    });
    root.querySelectorAll('[data-bulk-logo-position-cb]').forEach((cb) => {
      cb.addEventListener('change', refresh);
    });
    root.querySelectorAll('[data-bulk-sponsor-radio]').forEach((rb) => {
      rb.addEventListener('change', refresh);
    });
    root.querySelectorAll('[data-bulk-logo-application-method]').forEach((rb) => {
      rb.addEventListener('change', refresh);
    });
    const feeCb = root.querySelector('[data-bulk-logo-fee-cb]');
    if (feeCb) {
      feeCb.addEventListener('change', refresh);
    }
    root.addEventListener('input', (event) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        (target.matches('[data-bulk-line-prop="Initials"]') ||
          target.matches('[data-bulk-line-prop="Small Number"]'))
      ) {
        refresh();
      }
    });
    refresh();
  }

  /**
   * @param {HTMLElement} root
   */
  function isEmbroideryApplicationMethod(root) {
    const method = root.querySelector('[data-bulk-logo-application-method]:checked');
    if (method instanceof HTMLInputElement) {
      return method.value.toLowerCase() === 'embroidered';
    }
    const feeChoiceSelect = root.querySelector('[data-bulk-fee-choice-select]');
    if (feeChoiceSelect instanceof HTMLSelectElement && feeChoiceSelect.options.length) {
      const opt = feeChoiceSelect.options[feeChoiceSelect.selectedIndex];
      return /embroider/i.test((opt && opt.textContent) || '');
    }
    const feeChoiceHidden = root.querySelector('[data-bulk-fee-choice-hidden]');
    const selectedLabel =
      feeChoiceHidden instanceof HTMLInputElement ? String(feeChoiceHidden.value || '') : '';
    if (selectedLabel) {
      return /embroider/i.test(selectedLabel);
    }
    return false;
  }

  /**
   * @param {string} key
   */
  function isPrintOnlyPositionKey(key) {
    const k = String(key || '').trim().toLowerCase();
    return k === 'large_back' || k === 'big_back';
  }

  /**
   * @param {string} kitMode
   */
  function isTeamSponsorMode(kitMode) {
    const mode = String(kitMode || '').trim().toLowerCase();
    return mode === 'team_kit' || mode === 'training_kit_with_sponsor';
  }

  /**
   * @param {HTMLInputElement} cb
   * @param {string} kitMode
   */
  function positionAllowedForSetup(cb, embroidery, kitMode) {
    const key = (cb.getAttribute('data-bulk-position-key') || '').trim().toLowerCase();
    if (isTeamSponsorMode(kitMode)) {
      if (embroidery) {
        return key === 'left_sleeve' || key === 'right_sleeve';
      }
      return (
        key === 'left_sleeve' ||
        key === 'right_sleeve' ||
        key === 'centre_chest' ||
        key === 'center_chest' ||
        key === 'large_chest' ||
        key === 'large_back' ||
        key === 'big_back'
      );
    }
    if (embroidery && isPrintOnlyPositionKey(key)) {
      return false;
    }
    const methods = (cb.getAttribute('data-bulk-position-methods') || 'print,embroidery')
      .toLowerCase()
      .split(',')
      .map((s) => s.trim());
    return embroidery ? methods.includes('embroidery') : methods.includes('print');
  }

  /**
   * @param {HTMLElement} labelEl
   * @param {HTMLInputElement} cb
   * @param {boolean} allowed
   */
  function setLogoPositionChoiceAvailable(labelEl, cb, allowed) {
    if (allowed) {
      cb.disabled = false;
      labelEl.hidden = false;
      labelEl.classList.remove('BulkOrderTable-logoPositionChoice--excluded');
      labelEl.classList.remove('BulkOrderTable-logoPositionChoice--chest-blocked');
      labelEl.removeAttribute('aria-hidden');
    } else {
      cb.checked = false;
      cb.disabled = true;
      labelEl.hidden = true;
      labelEl.classList.add('BulkOrderTable-logoPositionChoice--excluded');
      labelEl.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * @param {HTMLElement | null} labelEl
   */
  function isLogoPositionChoiceSelectable(labelEl) {
    return (
      labelEl instanceof HTMLElement &&
      !labelEl.classList.contains('BulkOrderTable-logoPositionChoice--excluded') &&
      !labelEl.hidden
    );
  }

  /**
   * Left chest and right chest are mutually exclusive (one placement only).
   * @param {HTMLElement} scope
   * @param {HTMLElement | null} positionWrap
   */
  function syncChestPositionExclusivity(scope, positionWrap) {
    if (!(positionWrap instanceof HTMLElement) || positionWrap.hidden) {
      return;
    }

    /** @type {Array<[string, string]>} */
    const chestPairs = [['left_chest', 'right_chest']];

    chestPairs.forEach(([keyA, keyB]) => {
      const cbA = positionWrap.querySelector(
        `[data-bulk-logo-position-cb][data-bulk-position-key="${keyA}"]`
      );
      const cbB = positionWrap.querySelector(
        `[data-bulk-logo-position-cb][data-bulk-position-key="${keyB}"]`
      );
      if (!(cbA instanceof HTMLInputElement) || !(cbB instanceof HTMLInputElement)) {
        return;
      }

      const labelA = cbA.closest('[data-bulk-logo-position-choice]');
      const labelB = cbB.closest('[data-bulk-logo-position-choice]');

      [cbA, cbB].forEach((cb) => {
        const label = cb.closest('[data-bulk-logo-position-choice]');
        if (!(label instanceof HTMLElement)) {
          return;
        }
        if (!isLogoPositionChoiceSelectable(label)) {
          return;
        }
        label.classList.remove('BulkOrderTable-logoPositionChoice--chest-blocked');
        cb.disabled = false;
      });

      const availA = isLogoPositionChoiceSelectable(labelA);
      const availB = isLogoPositionChoiceSelectable(labelB);
      if (!availA || !availB) {
        return;
      }

      if (cbA.checked) {
        cbB.checked = false;
        cbB.disabled = true;
        if (labelB instanceof HTMLElement) {
          labelB.classList.add('BulkOrderTable-logoPositionChoice--chest-blocked');
        }
      } else if (cbB.checked) {
        cbA.checked = false;
        cbA.disabled = true;
        if (labelA instanceof HTMLElement) {
          labelA.classList.add('BulkOrderTable-logoPositionChoice--chest-blocked');
        }
      }
    });
  }

  /**
   * Show/hide position checkboxes and help text based on Print vs Embroidery setup choice.
   * @param {HTMLElement} root
   * @param {HTMLElement | null} scopePanel
   */
  function syncLogoPositionsForSetup(root, scopePanel) {
    const scope = scopePanel || root;
    const positionWrap = scope.querySelector('[data-bulk-logo-position-wrap]');

    const choiceEls =
      positionWrap instanceof HTMLElement
        ? positionWrap.querySelectorAll('[data-bulk-logo-position-choice]')
        : scope.querySelectorAll('[data-bulk-logo-position-choice]');

    choiceEls.forEach((labelEl) => {
      if (!(labelEl instanceof HTMLElement)) {
        return;
      }
      const cb = labelEl.querySelector('[data-bulk-logo-position-cb]');
      if (!(cb instanceof HTMLInputElement)) {
        return;
      }
      setLogoPositionChoiceAvailable(labelEl, cb, true);
    });

    scope.querySelectorAll('[data-bulk-logo-position-note]').forEach((noteEl) => {
      if (!(noteEl instanceof HTMLElement)) {
        return;
      }
      noteEl.hidden = true;
    });

    if (positionWrap instanceof HTMLElement && !positionWrap.hidden) {
      const legend = positionWrap.querySelector('.input__block-label');
      if (legend instanceof HTMLElement) {
        const base =
          root.getAttribute('data-bulk-logo-position-label') ||
          'Logo positions (select all that apply)';
        legend.textContent = base;
      }
    }

    syncChestPositionExclusivity(scope, positionWrap);
  }

  /**
   * @param {HTMLElement} root
   * @returns {Array<{ key: string; label: string }>}
   */
  function selectedPositionEntriesFromRoot(root) {
    /** @type {Array<{ key: string; label: string }>} */
    const out = [];
    root.querySelectorAll('[data-bulk-logo-position-cb]:checked').forEach((cb) => {
      if (!(cb instanceof HTMLInputElement)) {
        return;
      }
      if (cb.disabled) {
        return;
      }
      const key = (cb.getAttribute('data-bulk-position-key') || '').trim().toLowerCase();
      const label = (cb.value || '').trim();
      if (!key) {
        return;
      }
      out.push({ key, label });
    });
    return out;
  }

  /**
   * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
   */
  function totalGarmentQuantity(items) {
    return items.reduce((sum, it) => sum + (it.quantity || 0), 0);
  }

  /**
   * @param {Array<{ quantity: number; properties?: Record<string, string> }>} items
   * @param {string} propertyKey
   */
  function populatedItemPropertyQuantity(items, propertyKey) {
    return items.reduce((sum, item) => {
      const value = item.properties ? String(item.properties[propertyKey] || '').trim() : '';
      return value ? sum + (item.quantity || 0) : sum;
    }, 0);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} positionLabel
   * @returns {Record<string, string>}
   */
  function buildApplicationFeeLineProperties(root, positionLabel) {
    const appliesKey = (root.getAttribute('data-bulk-fee-applies-key') || 'Applies to').trim();
    const productTitle = (root.getAttribute('data-bulk-product-title') || '').trim() || 'Garment';
    return {
      'Logo position': positionLabel,
      [appliesKey]: productTitle,
    };
  }

  /**
   * @param {HTMLElement} root
   * @param {string} applicationLabel
   * @returns {Record<string, string>}
   */
  function buildPersonalisationFeeLineProperties(root, applicationLabel) {
    const appliesKey = (root.getAttribute('data-bulk-fee-applies-key') || 'Applies to').trim();
    const productTitle = (root.getAttribute('data-bulk-product-title') || '').trim() || 'Garment';
    return {
      Application: applicationLabel,
      [appliesKey]: productTitle,
    };
  }

  /**
   * One application fee line per selected position. The variant tier and line quantity
   * both use garmentQty; position count never participates in tier selection.
   * @param {HTMLElement} root
   * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
   * @param {HTMLInputElement[]} inputs
   * @returns {Array<{ id: number; quantity: number; properties?: Record<string, string> }>}
   */
  function buildApplicationFeeItems(root, items, inputs) {
    const garmentQty = totalGarmentQuantity(items);
    if (garmentQty < 1) {
      return [];
    }

    const embroidery = isEmbroideryApplicationMethod(root);
    const entries = selectedPositionEntriesFromRoot(root);
    if (!entries.length) {
      return [];
    }

    /** @type {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} */
    const feeItems = [];
    const tierIdx = applicationTierIndex(garmentQty);
    entries.forEach((entry) => {
      const large = LARGE_POSITION_KEYS.has(entry.key);
      const feeBucket = embroidery
        ? large
          ? 'embroideryLarge'
          : 'embroiderySmall'
        : large
          ? 'printLarge'
          : 'printSmall';
      const attr =
        feeBucket === 'printLarge'
          ? 'data-bulk-app-tier-print-large'
          : feeBucket === 'printSmall'
            ? 'data-bulk-app-tier-print-small'
            : feeBucket === 'embroideryLarge'
              ? 'data-bulk-app-tier-embroidery-large'
              : 'data-bulk-app-tier-embroidery-small';
      const tiers = parseTierVariantCsv(root, attr);
      const variantId = tiers[tierIdx];
      if (!variantId) {
        return;
      }
      feeItems.push({
        id: variantId,
        quantity: garmentQty,
        properties: {
          ...buildApplicationFeeLineProperties(root, entry.label),
          _kitwear_fee_type: 'application',
          _kitwear_fee_bucket: feeBucket,
          _kitwear_app_position_key: entry.key,
          _kitwear_default_application: 'true',
        },
      });
    });
    return feeItems;
  }

  /**
   * One fixed-price fee line per selected Training Wear position and one per populated
   * personalisation property type. Sponsor quantities use all garments; personalisation
   * quantities count only garment lines where that exact property is populated.
   * @param {HTMLElement} root
   * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
   * @returns {Array<{ id: number; quantity: number; properties?: Record<string, string> }>}
   */
  function buildTrainingWearApplicationFeeItems(root, items) {
    const garmentQty = totalGarmentQuantity(items);
    const fixedIds = readTrainingWearAppIds(root);
    const entries = selectedPositionEntriesFromRoot(root);
    if (garmentQty < 1 || !fixedIds) {
      return [];
    }

    /** @type {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} */
    const feeItems = [];
    const printConfigReady = fixedIds.printLarge > 0 && fixedIds.printSmall > 0;
    entries.forEach((entry) => {
      if (!printConfigReady) {
        return;
      }
      const large = TRAINING_WEAR_LARGE_POSITION_KEYS.has(entry.key);
      const small = TRAINING_WEAR_SMALL_POSITION_KEYS.has(entry.key);
      if (!large && !small) {
        return;
      }
      const feeBucket = large ? 'trainingPrintLarge' : 'trainingPrintSmall';
      const variantId = large ? fixedIds.printLarge : fixedIds.printSmall;
      if (!variantId) {
        return;
      }
      feeItems.push({
        id: variantId,
        quantity: garmentQty,
        properties: {
          ...buildApplicationFeeLineProperties(root, entry.label),
          _kitwear_fee_type: 'application',
          _kitwear_fee_bucket: feeBucket,
          _kitwear_app_position_key: entry.key,
          _kitwear_training_wear_application: 'true',
        },
      });
    });

    const personalisationEntries = [
      {
        bucket: 'trainingInitials',
        propertyKey: 'Initials',
        positionKey: 'initials',
        label: root.getAttribute('data-bulk-label-ini') || 'Initials',
      },
      {
        bucket: 'trainingSmallNumber',
        propertyKey: 'Small Number',
        positionKey: 'small_number',
        label: root.getAttribute('data-bulk-label-small-number') || 'Small Number',
      },
    ];
    personalisationEntries.forEach((entry) => {
      const quantity = populatedItemPropertyQuantity(items, entry.propertyKey);
      if (quantity < 1 || !fixedIds.personalisation || !fixedIds.personalisationAvailable) {
        return;
      }
      feeItems.push({
        id: fixedIds.personalisation,
        quantity,
        properties: {
          ...buildPersonalisationFeeLineProperties(root, entry.label),
          _kitwear_fee_type: 'application',
          _kitwear_fee_bucket: entry.bucket,
          _kitwear_app_position_key: entry.positionKey,
          _kitwear_training_wear_application: 'true',
        },
      });
    });
    return feeItems;
  }

  /**
   * @param {HTMLElement} root
   * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
   * @param {HTMLInputElement[]} inputs
   * @returns {{ item: { id: number; quantity: number; properties?: Record<string, string> } | null; variantMissing: boolean }}
   */
  function buildClubBadgeSurchargeItem(root, items, inputs) {
    if (!isClubBadgeFeatureEnabled(root) || !isClubBadgeEmbroideryChosen(root)) {
      return { item: null, variantMissing: false };
    }
    const vid = parseInt(root.getAttribute('data-bulk-club-badge-embroidery-variant-id') || '', 10);
    if (Number.isNaN(vid) || vid <= 0) {
      return { item: null, variantMissing: true };
    }
    const qty = totalGarmentQuantity(items);
    if (qty < 1) {
      return { item: null, variantMissing: false };
    }
    const setupText =
      (root.getAttribute('data-bulk-club-badge-setup-value') || 'Club badge embroidery surcharge').trim();
    const embroideredLabel =
      (root.getAttribute('data-bulk-club-badge-label-embroidered') || 'Embroidered badge').trim();
    return {
      variantMissing: false,
      item: {
        id: vid,
        quantity: qty,
        properties: {
          ...buildFeeLineProperties(items, inputs, root),
          Setup: setupText,
          'Club badge': embroideredLabel,
          _kitwear_fee_type: 'club_badge',
          _kitwear_fee_bucket: 'club_badge_embroidery',
        },
      },
    };
  }

  /**
   * @param {HTMLElement} root
   */
  function initClubBadgeSelector(root) {
    const panel = root.querySelector('[data-bulk-club-badge-panel]');
    if (!(panel instanceof HTMLElement)) {
      root.setAttribute('data-bulk-club-badge-method', 'printed');
      return;
    }
    const hidden = panel.querySelector('[data-bulk-club-badge-hidden]');
    const printedLabel = (root.getAttribute('data-bulk-club-badge-label-printed') || 'Printed badge').trim();
    const embroideredLabel =
      (root.getAttribute('data-bulk-club-badge-label-embroidered') || 'Embroidered badge').trim();
    const sync = () => {
      const selected = panel.querySelector('[data-bulk-club-badge-method]:checked');
      const method =
        selected instanceof HTMLInputElement && selected.value === 'embroidered'
          ? 'embroidered'
          : 'printed';
      root.setAttribute('data-bulk-club-badge-method', method);
      if (hidden instanceof HTMLInputElement) {
        hidden.value = method === 'embroidered' ? embroideredLabel : printedLabel;
      }
    };
    panel.addEventListener('change', (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      if (t.matches('[data-bulk-club-badge-method]')) {
        sync();
      }
    });
    sync();
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLInputElement[]} inputs
   */
  function rebuildPersonalizeList(root, inputs) {
    const flow = root.querySelector('[data-bulk-personalize-flow]');
    const list = root.querySelector('[data-bulk-personalize-list]');
    if (!flow || !list || !isPersonalizeActive(root)) {
      return;
    }

    let totalUnits = 0;
    for (const inp of inputs) {
      if (inp.disabled) {
        continue;
      }
      const q = parseInt(inp.value, 10) || 0;
      if (q > 0) {
        totalUnits += q;
      }
    }

    if (totalUnits === 0) {
      flow.hidden = true;
      list.innerHTML = '';
      return;
    }

    /** @type {Map<string, Record<string, string>>} */
    const previousUnitValues = new Map();
    list.querySelectorAll('[data-bulk-personalize-unit]').forEach((unitEl) => {
      if (!(unitEl instanceof HTMLElement)) {
        return;
      }
      const vid = (unitEl.getAttribute('data-variant-id') || '').trim();
      const idx = (unitEl.getAttribute('data-bulk-unit-index') || '').trim();
      if (!vid || !idx) {
        return;
      }
      /** @type {Record<string, string>} */
      const vals = {};
      unitEl.querySelectorAll('[data-bulk-line-prop]').forEach((field) => {
        if (!(field instanceof HTMLInputElement)) {
          return;
        }
        const propKey = field.getAttribute('data-bulk-line-prop');
        if (!propKey) {
          return;
        }
        vals[propKey] = field.value != null ? String(field.value) : '';
      });
      previousUnitValues.set(`${vid}::${idx}`, vals);
    });

    flow.hidden = false;
    list.innerHTML = '';

    const showTeam = root.getAttribute('data-bulk-show-team') === 'true';
    const showIni = root.getAttribute('data-bulk-show-ini') === 'true';
    const showSmallNumber = root.getAttribute('data-bulk-show-small-number') === 'true';
    const showName = root.getAttribute('data-bulk-show-name') === 'true';
    const tmpl =
      root.getAttribute('data-bulk-unit-label-template') || '{size} · item {index} of {total}';
    const lbTeam = root.getAttribute('data-bulk-label-team') || 'Team number';
    const lbIni = root.getAttribute('data-bulk-label-ini') || 'Initials';
    const lbSmallNumber = root.getAttribute('data-bulk-label-small-number') || 'Small Number';
    const lbName = root.getAttribute('data-bulk-label-name') || 'Player name';
    const phTeam = root.getAttribute('data-bulk-ph-team') || '';
    const phIni = root.getAttribute('data-bulk-ph-ini') || '';
    const phSmallNumber = root.getAttribute('data-bulk-ph-small-number') || '';
    const phName = root.getAttribute('data-bulk-ph-name') || '';

    /**
     * @param {string} propKey
     * @param {string} label
     * @param {string} placeholder
     * @param {number | undefined} maxLen
     * @param {string | undefined} initialValue
     */
    function makeField(propKey, label, placeholder, maxLen, initialValue) {
      const fid = 'bulk-prop-' + String(Math.random()).slice(2, 12);
      const outer = document.createElement('div');
      outer.className = 'BulkOrderTable-personalizeField';
      const lab = document.createElement('label');
      lab.className = 'input__block-label';
      lab.htmlFor = fid;
      lab.textContent = label;
      const inner = document.createElement('div');
      inner.className = 'input';
      const inp = document.createElement('input');
      inp.id = fid;
      inp.type = 'text';
      inp.className = 'input__field';
      inp.setAttribute('data-bulk-line-prop', propKey);
      inp.setAttribute('autocomplete', 'off');
      if (placeholder) {
        inp.placeholder = placeholder;
      }
      if (maxLen) {
        inp.maxLength = maxLen;
      }
      if (initialValue != null) {
        inp.value = normalizeLinePropertyValue(propKey, String(initialValue));
      }
      if (propKey === 'Initials') {
        inp.style.textTransform = 'uppercase';
        inp.setAttribute('autocapitalize', 'characters');
        inp.addEventListener('input', () => {
          inp.value = inp.value.toUpperCase();
        });
      }
      inner.appendChild(inp);
      outer.appendChild(lab);
      outer.appendChild(inner);
      return outer;
    }

    for (const qtyInput of inputs) {
      if (qtyInput.disabled) {
        continue;
      }
      const vid = qtyInput.getAttribute('data-variant-id');
      const qty = parseInt(qtyInput.value, 10) || 0;
      if (!vid || qty < 1) {
        continue;
      }
      const sizeLabel = qtyInput.getAttribute('data-size') || '';

      for (let u = 1; u <= qty; u++) {
        const unit = document.createElement('div');
        unit.className = 'BulkOrderTable-personalizeUnit';
        unit.setAttribute('data-bulk-personalize-unit', '');
        unit.setAttribute('data-variant-id', vid);
        unit.setAttribute('data-bulk-unit-index', String(u));

        const prev = previousUnitValues.get(`${vid}::${u}`) || {};

        const head = document.createElement('p');
        head.className = 'BulkOrderTable-personalizeUnit-label text--small text--subdued';
        head.textContent = tmpl
          .replace(/\{size\}/g, sizeLabel)
          .replace(/\{index\}/g, String(u))
          .replace(/\{total\}/g, String(qty));

        const fields = document.createElement('div');
        fields.className = 'BulkOrderTable-personalizeUnit-fields';
        if (showIni && showSmallNumber && isTrainingWearApplicationProduct(root)) {
          fields.classList.add('BulkOrderTable-personalizeUnit-fields--training-wear');
        }

        if (showTeam) {
          fields.appendChild(
            makeField('Team number', lbTeam, phTeam, undefined, prev['Team number'])
          );
        }
        if (showIni) {
          fields.appendChild(makeField('Initials', lbIni, phIni, 12, prev.Initials));
        }
        if (showSmallNumber) {
          fields.appendChild(
            makeField('Small Number', lbSmallNumber, phSmallNumber, 4, prev['Small Number'])
          );
        }
        if (showName) {
          fields.appendChild(makeField('Player name', lbName, phName, 80, prev['Player name']));
        }

        unit.appendChild(head);
        unit.appendChild(fields);
        list.appendChild(unit);
      }
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement | null} statusEl
   * @param {HTMLElement} loadingButton
   * @param {HTMLInputElement[]} inputs
   * @param {HTMLFormElement | null} mainForm
   */
  async function runBulkAddAttempt(root, statusEl, loadingButton, inputs, mainForm) {
    /**
     * @param {'error' | 'warning' | 'success' | 'info'} kind
     */
    function applyStatusKind(kind) {
      if (!statusEl) {
        return;
      }
      statusEl.classList.remove(
        'KitwearNotice--error',
        'KitwearNotice--warning',
        'KitwearNotice--success',
        'KitwearNotice--info'
      );
      statusEl.classList.add(`KitwearNotice--${kind}`);
    }

    /**
     * @param {string} message
     * @param {'error' | 'warning' | 'success' | 'info'} kind
     * @param {boolean} scroll
     */
    function setStatus(message, kind, scroll) {
      const msg = String(message || '').trim();
      if (!statusEl || !msg) {
        return;
      }
      applyStatusKind(kind);
      statusEl.textContent = msg;
      statusEl.hidden = false;
      if (scroll) {
        try {
          statusEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch {
          // ignore
        }
      }
    }

    /**
     * Ensure validation errors are clearly visible (inline + theme notification + scroll).
     * @param {string} message
     * @param {string} [source]
     */
    function showValidationError(message, source = '') {
      const err = String(message || '').trim();
      if (!err) {
        return;
      }
      if (statusEl) {
        if (source) {
          statusEl.dataset.kitwearStatusSource = source;
        } else {
          delete statusEl.dataset.kitwearStatusSource;
        }
      }
      setStatus(err, 'error', true);
      root.dispatchEvent(
        new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: {
            status: 'error',
            error: err,
          },
        })
      );
    }

    if (!inputs.some((input) => !input.disabled && (parseInt(input.value, 10) || 0) > 0)) {
      showValidationError(root.getAttribute('data-msg-empty') || '');
      return;
    }

    const newLogoChosen = isNewLogoChosen(root);
    const reusedLogoChosen = isReusedLogoChosen(root);
    const logoPricingSelectionActive = isLogoPricingSelectionActive(root);
    const artworkFeatureActive = isEligibleArtworkKitMode(root);
    const defaultApplicationActive = isDefaultApplicationProduct(root);
    const trainingWearApplicationActive = isTrainingWearApplicationProduct(root);
    let artworkMetadata = null;

    if (
      defaultApplicationActive &&
      !root.querySelector('[data-bulk-logo-application-method]:checked')
    ) {
      showValidationError(root.getAttribute('data-msg-logo-application-required') || '');
      return;
    }
    if (
      defaultApplicationActive &&
      !root.querySelector('[data-bulk-logo-position-cb]:checked')
    ) {
      showValidationError(root.getAttribute('data-msg-logo-position-required') || '');
      return;
    }

    artworkDebug('Add validation context', {
      kitMode: (root.getAttribute('data-bulk-kit-mode') || '').trim(),
      kitModeEligible: artworkFeatureActive,
      sponsorChoice: newLogoChosen ? 'new upload' : reusedLogoChosen ? 'reuse upload' : 'existing logo',
    });

    if (artworkFeatureActive && newLogoChosen) {
      try {
        artworkMetadata = await resolveNewArtworkMetadata(root, mainForm);
      } catch (error) {
        showValidationError(
          error instanceof Error
            ? error.message
            : root.getAttribute('data-msg-artwork-file-unavailable') || '',
          'logo-upload'
        );
        artworkDebug('New upload validation stopped add-to-cart', {
          reason: error instanceof Error ? error.message : String(error),
        });
        const gate = document.querySelector('[data-uploadly-sponsor-gate]');
        if (gate instanceof HTMLElement) {
          gate.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        return;
      }
    } else if (artworkFeatureActive && reusedLogoChosen) {
      artworkMetadata = selectedReusableArtwork(root);
      if (!artworkMetadata || !artworkMetadata.fileUrl || !artworkMetadata.uploadPropertyValue) {
        showValidationError(root.getAttribute('data-msg-logo-reuse-required') || 'Select a logo to reuse.');
        return;
      }
      if (!artworkMetadata.setupVariantId) {
        artworkMetadata = {
          ...artworkMetadata,
          setupVariantId: String(root.getAttribute('data-bulk-setup-fee-variant-id') || '').trim(),
        };
      }
    }

    if (
      artworkMetadata &&
      (!artworkMetadata.setupVariantId || !/^\d+$/.test(String(artworkMetadata.setupVariantId)))
    ) {
      artworkDebug('Validation stopped: setup variant unavailable', {
        setupVariantId: artworkMetadata.setupVariantId || '',
      });
      showValidationError(root.getAttribute('data-msg-setup-fee-pool-missing') || '');
      return;
    }

    artworkDebug('Building cart items');
    const items = buildCartItems(inputs, mainForm, root, artworkMetadata);

    if (!items.length) {
      showValidationError(root.getAttribute('data-msg-empty') || '');
      return;
    }

    if ((root.getAttribute('data-bulk-kit-mode') || '') === 'team_kit') {
      const newTeamNumbers = new Set();
      let hasDuplicateInSelection = false;
      items.forEach((it) => {
        const props = it.properties || {};
        const teamNum = normalizeTeamNumber(/** @type {Record<string, string>} */ (props)['Team number']);
        if (!teamNum) {
          return;
        }
        if (newTeamNumbers.has(teamNum)) {
          hasDuplicateInSelection = true;
          return;
        }
        newTeamNumbers.add(teamNum);
      });

      if (hasDuplicateInSelection) {
        showValidationError(root.getAttribute('data-msg-team-number-duplicate') || '');
        return;
      }

      if (newTeamNumbers.size > 0) {
        const productId = parseInt(root.getAttribute('data-bulk-product-id') || '', 10);
        if (!Number.isNaN(productId) && productId > 0) {
          try {
            const cartRes = await fetch(cartUrl, { credentials: 'same-origin' });
            const cartJson = await cartRes.json();
            const existingTeamNumbers = new Set();
            (cartJson.items || []).forEach((line) => {
              if (Number(line.product_id) !== productId) {
                return;
              }
              const props = normalizeLineItemProperties(line.properties || {});
              const teamNum = normalizeTeamNumber(props['Team number']);
              if (teamNum) {
                existingTeamNumbers.add(teamNum);
              }
            });
            const hasDuplicateInCart = Array.from(newTeamNumbers).some((n) =>
              existingTeamNumbers.has(n)
            );
            if (hasDuplicateInCart) {
              showValidationError(root.getAttribute('data-msg-team-number-duplicate') || '');
              return;
            }
          } catch {
            // If cart read fails, continue with normal add flow.
          }
        }
      }
    }

    if (logoPricingSelectionActive) {
      const posChecked = root.querySelectorAll('[data-bulk-logo-position-cb]:checked');
      if (!posChecked.length) {
        showValidationError(root.getAttribute('data-msg-sponsor-validation') || '');
        return;
      }
      if (newLogoChosen && !artworkFeatureActive) {
        const formForUpload = resolveMainForm(root, mainForm);
        if (!hasNewLogoUploadEvidence(formForUpload, root)) {
          showValidationError(root.getAttribute('data-msg-sponsor-upload-required') || '');
          return;
        }
      }
    }

    artworkDebug('Validation passed', {
      itemCount: items.length,
      uploadConsideredReady: !newLogoChosen || Boolean(artworkMetadata),
    });

    /** @type {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} */
    const baseItems = items.slice();
    /** Non-eligible legacy/workwear flows retain their existing per-bulk-group setup behaviour. */
    const feeItems = [];
    if (defaultApplicationActive) {
      const selectedPositionCount = selectedPositionEntriesFromRoot(root).length;
      const applicationItems = buildApplicationFeeItems(root, baseItems, inputs);
      if (applicationItems.length !== selectedPositionCount) {
        showValidationError(root.getAttribute('data-msg-app-tiers-missing') || '');
        return;
      }
      feeItems.push(...applicationItems);
    }
    if (trainingWearApplicationActive) {
      const selectedPositionCount = selectedPositionEntriesFromRoot(root).length;
      const populatedInitialsCount = populatedItemPropertyQuantity(baseItems, 'Initials');
      const populatedSmallNumberCount = populatedItemPropertyQuantity(baseItems, 'Small Number');
      const requiredFeeLineCount =
        selectedPositionCount +
        (populatedInitialsCount > 0 ? 1 : 0) +
        (populatedSmallNumberCount > 0 ? 1 : 0);
      const applicationItems = buildTrainingWearApplicationFeeItems(root, baseItems);
      if (applicationItems.length !== requiredFeeLineCount) {
        showValidationError(root.getAttribute('data-msg-training-wear-app-missing') || '');
        return;
      }
      feeItems.push(...applicationItems);
    }
    if (!artworkFeatureActive && root.getAttribute('data-bulk-fee-required') === 'true') {
      const vid = parseInt(root.getAttribute('data-bulk-setup-fee-variant-id') || '', 10);
      if (!Number.isNaN(vid) && vid > 0) {
        feeItems.push({
          id: vid,
          quantity: 1,
          properties: {
            ...buildFeeLineProperties(baseItems, inputs, root),
            _kitwear_fee_type: 'setup',
          },
        });
      }
    }
    if (
      !artworkFeatureActive &&
      newLogoChosen &&
      root.getAttribute('data-bulk-fee-required') !== 'true'
    ) {
      showValidationError(root.getAttribute('data-msg-setup-fee-pool-missing') || '');
      return;
    }

    const bulkGroupId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : 'kw-' + Date.now() + '-' + Math.random().toString(36).slice(2, 12);

    /**
     * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} arr
     */
    function withBulkGroup(arr) {
      return arr.map((item) => {
        const props = item.properties ? { ...item.properties } : {};
        if (props._kitwear_fee_type === 'application') {
          // Fee reconciliation metadata stays private; only Logo position and Applies to are public.
          props._kitwear_bulk_group = bulkGroupId;
          delete props.kitwear_bulk_group;
        } else {
          // Preserve the established public garment/club grouping property.
          props.kitwear_bulk_group = bulkGroupId;
        }
        return { ...item, properties: props };
      });
    }

    const mainPayload = withBulkGroup(baseItems);
    const feePayload = feeItems.length ? withBulkGroup(feeItems) : null;

    if (statusEl) {
      statusEl.hidden = true;
      statusEl.textContent = '';
      delete statusEl.dataset.kitwearStatusSource;
      applyStatusKind('info');
    }

    const kitwearCart = window.KitwearCartBulk;
    if (kitwearCart && typeof kitwearCart.suppressCartMaintenance === 'function') {
      kitwearCart.suppressCartMaintenance(4000);
    }

    const addOptions = { bulkGroupId };

    /**
     * @param {unknown} responseJson
     */
    async function afterAddSuccess(responseJson) {
      let finalResponseJson = responseJson;
      if (defaultApplicationActive || trainingWearApplicationActive) {
        if (!kitwearCart || typeof kitwearCart.syncFeeLinesInCart !== 'function') {
          await rollbackBulkGroupLines(bulkGroupId);
          throw new Error(root.getAttribute('data-msg-application-fee-failed') || '');
        }
        try {
          const cartForSync = await fetchCartJson();
          if (!cartForSync) {
            throw new Error('Could not read the cart after adding application charges.');
          }
          const synced = await kitwearCart.syncFeeLinesInCart(cartForSync);
          finalResponseJson = synced || cartForSync;
        } catch (syncError) {
          await rollbackBulkGroupLines(bulkGroupId);
          try {
            await kitwearCart.syncLogoSetupFees();
          } catch {
            // The next cart event will remove any central setup charge left by this failed group.
          }
          throw new Error(root.getAttribute('data-msg-application-fee-failed') || String(syncError));
        }
      } else if (artworkMetadata) {
        if (!kitwearCart || typeof kitwearCart.syncLogoSetupFees !== 'function') {
          await rollbackBulkGroupLines(bulkGroupId);
          throw new Error(root.getAttribute('data-msg-setup-fee-failed') || 'Could not synchronise the logo setup fee.');
        }
        try {
          artworkDebug('Running setup reconciliation');
          const synced = await kitwearCart.syncLogoSetupFees();
          artworkDebug('Setup reconciliation completed', { success: true });
          if (synced) {
            finalResponseJson = synced;
          }
        } catch (syncError) {
          artworkDebug('Setup reconciliation failed', {
            reason: syncError instanceof Error ? syncError.message : String(syncError),
          });
          await rollbackBulkGroupLines(bulkGroupId);
          try {
            await kitwearCart.syncLogoSetupFees();
          } catch {
            // The next cart event/cart-page load will reconcile any remaining setup line.
          }
          throw syncError;
        }
      }
      const afterAdd = root.getAttribute('data-bulk-after-add') || 'follow_theme';
      if (afterAdd === 'go_to_cart' && isPersonalizeActive(root)) {
        window.location.assign(window.themeVariables.routes.cartUrl);
        return;
      }
      try {
        await refreshCartUi(/** @type {Record<string, unknown>} */ (finalResponseJson));
      } catch (refreshErr) {
        console.error('[kitwear bulk add] cart UI refresh failed', refreshErr);
      }
      root.dispatchEvent(
        new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: {
            status: 'success',
            error: '',
          },
        })
      );
      for (const input of inputs) {
        if (!input.disabled) {
          input.value = '0';
        }
      }
      rebuildPersonalizeList(root, inputs);
    }

    /**
     * @param {string} err
     */
    function showAddError(err) {
      if (statusEl) {
        delete statusEl.dataset.kitwearStatusSource;
      }
      setStatus(err, 'error', false);
      root.dispatchEvent(
        new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: {
            status: 'error',
            error: err,
          },
        })
      );
    }

    try {
      if (feePayload) {
        artworkDebug('Calling /cart/add.js', { itemCount: mainPayload.length, payload: 'garments' });
        const garmentsResult = await postCartAddItems(mainPayload, addOptions);
        artworkDebug('Cart response received', {
          status: garmentsResult.response.status,
          ok: garmentsResult.ok,
          payload: 'garments',
        });
        if (!garmentsResult.ok) {
          showAddError(
            cartAddFailureMessage(
              garmentsResult.responseJson,
              garmentsResult.response.status,
              root
            )
          );
          return;
        }
        artworkDebug('Calling /cart/add.js', { itemCount: feePayload.length, payload: 'linked fees' });
        const feeResult = await postCartAddItems(feePayload, addOptions);
        artworkDebug('Cart response received', {
          status: feeResult.response.status,
          ok: feeResult.ok,
          payload: 'linked fees',
        });
        if (!feeResult.ok) {
          await rollbackBulkGroupLines(bulkGroupId);
          showAddError(
            defaultApplicationActive || trainingWearApplicationActive
              ? root.getAttribute('data-msg-application-fee-failed') || 'Could not add application charges.'
              : root.getAttribute('data-msg-setup-fee-failed') || 'Could not add the setup fee.'
          );
          return;
        }
        await afterAddSuccess(feeResult.responseJson);
        return;
      }

      artworkDebug('Calling /cart/add.js', { itemCount: mainPayload.length, payload: 'garments' });
      const r = await postCartAddItems(mainPayload, addOptions);
      artworkDebug('Cart response received', {
        status: r.response.status,
        ok: r.ok,
        payload: 'garments',
      });
      if (r.ok) {
        await afterAddSuccess(r.responseJson);
      } else {
        showAddError(cartAddFailureMessage(r.responseJson, r.response.status, root));
      }
    } catch (err) {
      console.error('[Kitwear Logo] Add-to-cart exception', err);
      const detail =
        err instanceof Error && err.message
          ? err.message
          : root.getAttribute('data-msg-error') || 'Could not add to cart';
      showAddError(detail);
    }
  }

  /**
   * One deliberate Add action owns the readiness wait and always restores the button.
   * @param {HTMLElement} root
   * @param {HTMLElement | null} statusEl
   * @param {HTMLElement} loadingButton
   * @param {HTMLInputElement[]} inputs
   * @param {HTMLFormElement | null} mainForm
   */
  async function runBulkAdd(root, statusEl, loadingButton, inputs, mainForm) {
    artworkDebug('Add clicked', {
      kitModeEligible: isEligibleArtworkKitMode(root),
      sponsorChoice: isNewLogoChosen(root)
        ? 'new upload'
        : isReusedLogoChosen(root)
          ? 'reuse upload'
          : 'existing logo',
    });
    if (bulkAddsInFlight.has(root)) {
      artworkDebug('Additional Add action ignored while the current attempt is still processing');
      return;
    }
    bulkAddsInFlight.add(root);
    loadingButton.setAttribute('disabled', 'disabled');
    loadingButton.setAttribute('aria-busy', 'true');
    try {
      await runBulkAddAttempt(root, statusEl, loadingButton, inputs, mainForm);
    } catch (error) {
      console.error('[Kitwear Logo] Unhandled add-to-cart exception', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : root.getAttribute('data-msg-error') || 'Could not add to cart';
      if (statusEl) {
        delete statusEl.dataset.kitwearStatusSource;
        statusEl.classList.remove(
          'KitwearNotice--warning',
          'KitwearNotice--success',
          'KitwearNotice--info'
        );
        statusEl.classList.add('KitwearNotice--error');
        statusEl.textContent = message;
        statusEl.hidden = false;
      }
      root.dispatchEvent(
        new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'error', error: message },
        })
      );
    } finally {
      loadingButton.removeAttribute('disabled');
      loadingButton.removeAttribute('aria-busy');
      bulkAddsInFlight.delete(root);
      artworkDebug('Add flow finished; button restored');
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function bind(root) {
    const idxConf = parseIndices(root);
    const rows = parseVariantMap(root);
    const colorSelect = root.querySelector('[data-bulk-color-select]');
    const extraSelects = Array.from(root.querySelectorAll('[data-bulk-extra-select]'));
    const inputs = Array.from(root.querySelectorAll('[data-bulk-qty-input]'));
    const bulkSubmit = root.querySelector('[data-bulk-add-to-cart]');
    const statusEl = root.querySelector('[data-bulk-status]');

    if (!inputs.length) {
      return;
    }

    const formWrapper = root.closest('.product-form');
    const mainForm = formWrapper && formWrapper.querySelector('form[is="product-form"]');
    const useMainProductAtc = Boolean(mainForm && formWrapper && formWrapper.contains(root));
    let deliberateAddClickAt = 0;
    if (formWrapper) {
      formWrapper.addEventListener(
        'click',
        (event) => {
          const target = event.target instanceof Element ? event.target.closest(
            '[data-product-add-to-cart-button], [data-clone-button="true"], .ph_clone_atc_btn'
          ) : null;
          if (!target) {
            return;
          }
          if (event.isTrusted) {
            deliberateAddClickAt = Date.now();
            artworkDebug('Trusted Add button intent captured');
          } else {
            artworkDebug('Synthetic Add button click observed; waiting for explicit customer intent');
          }
        },
        true
      );
    }
    initArtworkReadinessTracking(root);
    /**
     * With bulk sizes active, keep native variant picker for colour/images but hide native size controls.
     * If a size chart page is configured, keep the chart link visible (controls stay in DOM for variant-picker).
     */
    function syncNativeVariantSizeVisibility() {
      if (!formWrapper) {
        return;
      }
      const variantPicker = formWrapper.querySelector('variant-picker.product-form__variants');
      if (!variantPicker) {
        return;
      }
      const sizeSelectors = variantPicker.querySelectorAll('.product-form__option-selector[data-is-size-option="true"]');
      sizeSelectors.forEach((el) => {
        const box = /** @type {HTMLElement} */ (el);
        const values = box.querySelector('.product-form__option-values');
        const info = box.querySelector('.product-form__option-info');
        const sizeChartLink = box.querySelector('.product-form__option-link[aria-controls*="size-chart"]');

        if (values) {
          values.style.display = 'none';
          values.setAttribute('aria-hidden', 'true');
        }

        if (sizeChartLink) {
          box.style.display = '';
          box.removeAttribute('aria-hidden');
          box.classList.add('product-form__option-selector--size-chart-only');
          if (info) {
            info.querySelectorAll('.product-form__option-name, .product-form__option-value').forEach((label) => {
              /** @type {HTMLElement} */ (label).style.display = 'none';
            });
          }
          return;
        }

        box.classList.remove('product-form__option-selector--size-chart-only');
        box.style.display = 'none';
        box.setAttribute('aria-hidden', 'true');
      });
    }

    function installNativeSizeHideObserver() {
      if (!formWrapper) {
        return;
      }
      if (formWrapper.dataset.bulkNativeSizeObserverBound === 'true') {
        return;
      }
      formWrapper.dataset.bulkNativeSizeObserverBound = 'true';
      const apply = () => {
        syncNativeVariantSizeVisibility();
      };
      const observer = new MutationObserver(() => {
        apply();
      });
      observer.observe(formWrapper, { childList: true, subtree: true });
      document.addEventListener('variant:changed', (event) => {
        const formTarget = /** @type {HTMLElement | null} */ (event.target);
        if (!formTarget || formTarget !== mainForm) {
          return;
        }
        // Theme updates can run after this event; re-apply on next frame.
        requestAnimationFrame(apply);
      });
      requestAnimationFrame(apply);
    }


    if (!useMainProductAtc && !bulkSubmit) {
      return;
    }

    /**
     * Embedded bulk blocks can use the native variant picker color (selected variant in main form).
     * @returns {number}
     */
    function selectedVariantIdFromMainForm() {
      if (!mainForm) {
        return NaN;
      }
      const idInput = mainForm.querySelector('input[name="id"], select[name="id"]');
      if (!(idInput instanceof HTMLInputElement || idInput instanceof HTMLSelectElement)) {
        return NaN;
      }
      const v = parseInt(idInput.value || '', 10);
      return Number.isNaN(v) ? NaN : v;
    }

    function currentColor() {
      if (!idxConf.useColor) {
        return '';
      }
      if (colorSelect) {
        return /** @type {HTMLSelectElement} */ (colorSelect).value || '';
      }
      if (useMainProductAtc) {
        const selectedId = selectedVariantIdFromMainForm();
        if (!Number.isNaN(selectedId)) {
          const row = rows.find((r) => Number(r.id) === selectedId);
          if (row && Array.isArray(row.opts)) {
            const c = row.opts[idxConf.colorIdx];
            return c != null ? String(c) : '';
          }
        }
      }
      return '';
    }

    function setQtyWrapDisabled(input, disabled) {
      const wrap = input.closest('[data-bulk-qty-wrap]');
      if (!wrap) {
        return;
      }
      const minus = wrap.querySelector('[data-bulk-qty-minus]');
      const plus = wrap.querySelector('[data-bulk-qty-plus]');
      if (minus) {
        minus.disabled = disabled;
      }
      if (plus) {
        plus.disabled = disabled;
      }
    }

    /**
     * @param {HTMLInputElement} input
     * @param {number} priceCents
     */
    function setSizeCardPrice(input, priceCents) {
      const card = input.closest('.BulkOrderTable-sizeCard');
      if (!(card instanceof HTMLElement)) {
        return;
      }
      const priceEl = card.querySelector('[data-bulk-size-price]');
      if (!(priceEl instanceof HTMLElement)) {
        return;
      }
      if (priceCents > 0) {
        priceEl.textContent = formatMoneyFromCents(priceCents);
        priceEl.hidden = false;
      } else {
        priceEl.textContent = '';
        priceEl.hidden = true;
      }
    }

    function syncQtyInputs() {
      const color = currentColor();
      const extras = getExtraSelections(root);
      const sizes = inputs.map((el) => el.getAttribute('data-size') || '');
      for (let i = 0; i < inputs.length; i++) {
        const row = findVariantRow(rows, sizes[i], color, extras, idxConf);
        const input = inputs[i];
        if (!row) {
          input.removeAttribute('data-variant-id');
          input.removeAttribute('data-variant-price-cents');
          input.disabled = true;
          input.value = '0';
          setQtyWrapDisabled(input, true);
          setSizeCardPrice(input, 0);
          continue;
        }
        const id = row.id;
        const priceCents = parseInt(String(row.priceCents != null ? row.priceCents : '0'), 10) || 0;
        input.setAttribute('data-variant-id', String(id));
        input.setAttribute('data-variant-price-cents', String(priceCents));
        input.disabled = !row.available;
        if (input.disabled) {
          input.value = '0';
        }
        setQtyWrapDisabled(input, input.disabled);
        setSizeCardPrice(input, priceCents);
      }
      rebuildPersonalizeList(root, inputs);
      updateOrderSummary(root, inputs);
      if (useMainProductAtc) {
        // Variant picker DOM can re-render on option change; keep native size selector hidden.
        syncNativeVariantSizeVisibility();
      }
    }

    root.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-bulk-qty-minus]');
      const plus = e.target.closest('[data-bulk-qty-plus]');
      if (!minus && !plus) {
        return;
      }
      const wrap = (minus || plus).closest('[data-bulk-qty-wrap]');
      const input = wrap && wrap.querySelector('[data-bulk-qty-input]');
      if (!input || input.disabled) {
        return;
      }
      const max = parseInt(input.getAttribute('max'), 10);
      const cap = Number.isFinite(max) && max > 0 ? max : 9999;
      let v = parseInt(input.value, 10) || 0;
      if (minus) {
        v = Math.max(0, v - 1);
      }
      if (plus) {
        v = Math.min(cap, v + 1);
      }
      input.value = String(v);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      rebuildPersonalizeList(root, inputs);
    });

    root.addEventListener(
      'input',
      (e) => {
        const t = e.target;
        if (t && /** @type {HTMLElement} */ (t).matches('[data-bulk-qty-input]')) {
          rebuildPersonalizeList(root, inputs);
        }
      },
      true
    );

    if (colorSelect) {
      colorSelect.addEventListener('change', syncQtyInputs);
    }
    if (useMainProductAtc && idxConf.useColor && mainForm) {
      // Native picker updates selected variant id on form changes; keep bulk size grid in sync.
      mainForm.addEventListener('change', syncQtyInputs, true);
      mainForm.addEventListener('input', syncQtyInputs, true);
    }
    extraSelects.forEach((sel) => {
      sel.addEventListener('change', syncQtyInputs);
    });
    syncQtyInputs();
    initGarmentVolumePricing(root, inputs);
    initSponsorPanel(root);
    initLogoApplicationMethod(root);
    refreshReusableArtworks(root).catch(() => {
      renderReusableArtworks(root, new Map());
    });
    document.documentElement.addEventListener('cart:updated', () => {
      refreshReusableArtworks(root).catch(() => {});
    });
    initOrderSummary(root, inputs);
    if (useMainProductAtc) {
      installNativeSizeHideObserver();
      syncNativeVariantSizeVisibility();
    }

    if (useMainProductAtc && mainForm) {
      mainForm.addEventListener(
        'submit',
        (e) => {
          const sub = e.submitter;
          if (!sub || !sub.matches('[data-product-add-to-cart-button]')) {
            return;
          }
          e.preventDefault();
          e.stopImmediatePropagation();
          const deliberate = deliberateAddClickAt > 0 && Date.now() - deliberateAddClickAt < 2500;
          deliberateAddClickAt = 0;
          if (!deliberate) {
            artworkDebug('Synthetic/non-customer product-form submission blocked without showing validation', {
              submitEventTrusted: e.isTrusted,
            });
            return;
          }
          runBulkAdd(root, statusEl, sub, inputs, mainForm);
        },
        true
      );
    }

    if (bulkSubmit) {
      bulkSubmit.addEventListener('click', (event) => {
        if (!event.isTrusted) {
          artworkDebug('Synthetic bulk Add click blocked without showing validation');
          return;
        }
        runBulkAdd(root, statusEl, bulkSubmit, inputs, useMainProductAtc ? mainForm : null);
      });
    }
  }

  for (const root of roots) {
    root.setAttribute('data-bulk-order-initialized', '');
    bind(root);
  }
})();
